// Import Firebase Functions API
import { tmdbFunctionsService, shouldUseFunctionsAPI, discoverMovies } from './tmdbFunctionsApi';
import { formatFirebaseDate } from '../utils/dateUtils';
// Import shared constants for consistency
import {
  TMDB_BASE_URL,
  TMDB_IMAGE_BASE_URL,
  formatMovieDisplayTitleFromId,
  getTmdbNumericIdForCache,
} from '@watchlist/shared';
// Import enhanced query processing
import QueryProcessor, { EnhancedQueryAnalysis } from '../utils/queryProcessor';
// Import request queue for caching (local cache -> Firestore cache -> TMDB API)
import { tmdbRequestQueue } from './tmdbRequestQueue';

export interface TMDbMovie {
  id: number;
  imdb_id?: string;
  title: string;
  original_title: string;
  release_date: string;
  year?: string; // Derived from release_date for compatibility
  overview: string;
  poster_path: string | null;
  backdrop_path: string | null;
  genre_ids: number[];
  genres?: Array<{
    id: number;
    name: string;
  }>;
  adult: boolean;
  video: boolean; // Added missing video property for shared compatibility
  original_language: string;
  popularity: number;
  vote_average: number;
  vote_count: number;
  runtime?: number;
  status?: string;
  tagline?: string;
  production_companies?: Array<{
    id: number;
    name: string;
    logo_path: string | null;
    origin_country: string;
  }>;
  production_countries?: Array<{
    iso_3166_1: string;
    name: string;
  }>;
  spoken_languages?: Array<{
    iso_639_1: string;
    name: string;
  }>;
  credits?: {
    cast: Array<{
      id: number;
      name: string;
      character: string;
      profile_path: string | null;
      order: number;
    }>;
    crew: Array<{
      id: number;
      name: string;
      job: string;
      department: string;
      profile_path: string | null;
    }>;
  };
  images?: {
    posters: Array<{
      file_path: string;
      width: number;
      height: number;
      aspect_ratio: number;
      vote_average: number;
      vote_count: number;
    }>;
    backdrops: Array<{
      file_path: string;
      width: number;
      height: number;
      aspect_ratio: number;
      vote_average: number;
      vote_count: number;
    }>;
  };
  // Standard compatibility fields
  Title?: string;
  Year?: string;
  Rated?: string;
  Released?: string;
  Runtime?: string;
  Genre?: string;
  Director?: string;
  Writer?: string;
  Actors?: string;
  Plot?: string;
  Language?: string;
  Country?: string;
  Awards?: string;
  Poster?: string;
  Ratings?: Array<{
    Source: string;
    Value: string;
  }>;
  Metascore?: string;
  imdbRating?: string;
  imdbVotes?: string;
  Type?: string;
  DVD?: string;
  BoxOffice?: string;
  Production?: string;
  Website?: string;
  Response?: string;
  imdbID?: string;
}

export interface TMDbMovieGenre {
  id: number;
  name: string;
}

export interface GenreWithMovies extends TMDbMovieGenre {
  movieCount?: number;
  topMovies?: TMDbMovie[];
  posterUrls?: string[];
}

export interface TMDbPerson {
  id: number;
  name: string;
  biography: string;
  birthday: string | null;
  deathday: string | null;
  place_of_birth: string | null;
  profile_path: string | null;
  adult: boolean;
  imdb_id: string | null;
  homepage: string | null;
  known_for_department: string;
  popularity: number;
  gender: 0 | 1 | 2 | 3; // 0: Not set, 1: Female, 2: Male, 3: Non-binary
  also_known_as: string[];
  external_ids?: {
    imdb_id: string | null;
    facebook_id: string | null;
    instagram_id: string | null;
    twitter_id: string | null;
  };
  images?: {
    profiles: Array<{
      file_path: string;
      width: number;
      height: number;
      aspect_ratio: number;
      vote_average: number;
      vote_count: number;
    }>;
  };
  movie_credits?: {
    cast: Array<{
      id: number;
      title: string;
      character: string;
      poster_path: string | null;
      release_date: string;
      vote_average: number;
      adult: boolean;
      credit_id: string;
      order?: number;
      popularity?: number;
      vote_count?: number;
    }>;
    crew: Array<{
      id: number;
      title: string;
      job: string;
      department: string;
      poster_path: string | null;
      release_date: string;
      vote_average: number;
      adult: boolean;
      credit_id: string;
      popularity?: number;
      vote_count?: number;
    }>;
  };
  combined_credits?: {
    cast: Array<{
      id: number;
      title: string;
      character: string;
      poster_path: string | null;
      backdrop_path: string | null;
      release_date: string;
      vote_average: number;
      media_type: 'movie' | 'tv';
      adult: boolean;
      credit_id: string;
      order?: number;
      popularity?: number;
      vote_count?: number;
    }>;
    crew: Array<{
      id: number;
      title: string;
      job: string;
      department: string;
      poster_path: string | null;
      backdrop_path: string | null;
      release_date: string;
      vote_average: number;
      media_type: 'movie' | 'tv';
      adult: boolean;
      credit_id: string;
      popularity?: number;
      vote_count?: number;
    }>;
  };
}

export interface TMDbPersonSearchResult {
  page: number;
  results: Array<{
    id: number;
    name: string;
    profile_path: string | null;
    adult: boolean;
    popularity: number;
    known_for_department: string;
    gender: 0 | 1 | 2 | 3;
    known_for: Array<{
      id: number;
      title?: string;
      name?: string;
      poster_path: string | null;
      media_type: 'movie' | 'tv';
    }>;
  }>;
  total_pages: number;
  total_results: number;
}

export interface TMDbSearchResult {
  page: number;
  results: TMDbMovie[];
  total_pages: number;
  total_results: number;
}

// Enhanced search query interface for advanced search
export interface AdvancedSearchQuery {
  title?: string;
  year?: string | number;
  cast?: string[];
  director?: string;
  genre?: string;
  combined?: string; // For natural language queries
}

export interface QueryAnalysis {
  originalQuery: string;
  actors: string[];
  directors: string[];
  detectedNames?: string[];
  title?: string;
  year?: string | number;
  genre?: string;
  productionCompany?: string;
  language?: string;
  keywords: string[];
  queryType: 'person-focused' | 'title-focused' | 'genre-focused' | 'production-focused' | 'language-focused' | 'mixed' | 'general';
  confidence: number; // 0-1 scale
}

export interface SearchStrategy {
  type: 'actor' | 'director' | 'title' | 'genre' | 'production' | 'language' | 'keyword' | 'title_with_language' | 'combined_term' | 'multi_keyword';
  value: string;
  priority: number; // 1-10 scale, 10 being highest priority
  confidence: number; // 0-1 scale
  language?: string; // Language code for language-specific searches
}

// Search result with relevance scoring
export interface EnhancedSearchResult {
  movie: TMDbMovie;
  relevanceScore: number;
  matchedFields: string[];
}

export interface EnhancedSearchResponse {
  Search: EnhancedSearchResult[];
  totalResults: string;
  Response: string;
  hasMore?: boolean;
  actualTotal?: number;
  currentPage?: number;
  totalPages?: number;
}

class TMDbService {
  private apiKey: string;
  private apiKeyValid: boolean = true;
  private lastValidationTime: number = 0;
  private validationCacheDuration: number = 5 * 60 * 1000; // 5 minutes
  private searchCache: Map<string, { results: any; timestamp: number }> = new Map();
  private cacheExpiryTime: number = 10 * 60 * 1000; // 10 minutes
  private searchAnalytics: Map<string, { count: number; lastSearched: number }> = new Map();
  private static readonly TITLE_STOP_WORDS = new Set<string>([
    'the',
    'a',
    'an',
    'and',
    'or',
    'of',
    'for',
    'with',
    'to',
    'in',
    'on',
    'at',
    'by'
  ]);

  constructor() {
    // All TMDB requests now route through Firebase Functions for security and rate limiting
    this.apiKey = ''; // No longer used - kept for backward compatibility
    console.log('🔒 TMDb API initialized - using Firebase Functions for secure API access');
    console.log('✅ All TMDB requests will be routed through Firebase Functions (no client-side API key needed)');
    this.apiKeyValid = true; // Functions API handles validation
  }

  // OMDb integration removed - using TMDB only for all movie data

  // Check if API key is valid
  private async validateApiKey(): Promise<boolean> {
    const now = Date.now();
    
    if (now - this.lastValidationTime < this.validationCacheDuration) {
      return this.apiKeyValid;
    }

    try {
      const response = await fetch(
        `${TMDB_BASE_URL}/configuration?api_key=${this.apiKey}`
      );
      
      if (response.status === 401) {
        this.apiKeyValid = false;
        console.warn('TMDb API key is invalid. Movie data will be limited.');
      } else {
        this.apiKeyValid = true;
      }
      
      this.lastValidationTime = now;
      return this.apiKeyValid;
    } catch (error) {
      console.warn('Failed to validate TMDb API key:', error);
      return this.apiKeyValid;
    }
  }

  // Convert TMDb movie to standardized format for existing code compatibility
  private async convertToStandardFormat(tmdbMovie: TMDbMovie): Promise<TMDbMovie> {
    console.log(`🔄 Converting movie to standard format:`, { 
      title: tmdbMovie.title, 
      release_date: tmdbMovie.release_date,
      id: tmdbMovie.id 
    });
    
    // Improved data extraction with guaranteed non-empty values
    const year = tmdbMovie.release_date && tmdbMovie.release_date.trim() 
      ? tmdbMovie.release_date.split('-')[0] 
      : new Date().getFullYear().toString(); // Current year as fallback
    
    // Only include runtime if it's actually available from the API
    const runtime = tmdbMovie.runtime && tmdbMovie.runtime > 0
      ? `${Math.floor(tmdbMovie.runtime / 60)}h ${tmdbMovie.runtime % 60}m` 
      : undefined; // Let StandardMovieCard handle missing runtime
    
    const genres = (tmdbMovie.genres && tmdbMovie.genres.length > 0) 
      ? tmdbMovie.genres.map(g => g.name).join('/') 
      : 'Drama'; // Always provide a genre
    
    // Get comprehensive language and country info with guaranteed fallbacks
    const languages = tmdbMovie.spoken_languages && tmdbMovie.spoken_languages.length > 0 
      ? tmdbMovie.spoken_languages.map(lang => lang.name).join(', ')
      : 'English';
    
    const countries = tmdbMovie.production_countries && tmdbMovie.production_countries.length > 0
      ? tmdbMovie.production_countries.map(country => country.name).join(', ')
      : 'United States';
    
    // Ensure we always have a valid title
    const title = tmdbMovie.title && tmdbMovie.title.trim()
      ? tmdbMovie.title.trim()
      : formatMovieDisplayTitleFromId(String(tmdbMovie.id));
    
    // Get director and main actors from credits with clean fallbacks
    let director = 'Unknown Director';
    let actors = 'Cast information not available';
    let writer = 'Unknown Writer';
    
    if (tmdbMovie.credits) {
      // Find all directors
      const directors = tmdbMovie.credits.crew.filter(person => person.job === 'Director');
      director = directors.length > 0 ? directors.map(d => d.name).join(', ') : 'Unknown Director';
      
      // Find writers, screenplay writers, and story writers
      const writers = tmdbMovie.credits.crew.filter(person => 
        ['Writer', 'Screenplay', 'Story', 'Novel', 'Book'].includes(person.job)
      );
      writer = writers.length > 0 ? writers.slice(0, 3).map(w => w.name).join(', ') : 'Unknown Writer';
      
      // Get top 6 cast members
      actors = tmdbMovie.credits.cast.length > 0
        ? tmdbMovie.credits.cast
            .slice(0, 6)
            .map(actor => actor.name)
            .join(', ')
        : 'Cast information not available';
    }

    // Use TMDb rating only - OMDb integration removed for performance
    const validVoteAverage = tmdbMovie.vote_average && tmdbMovie.vote_average > 0
      ? tmdbMovie.vote_average
      : 7.0; // Default to a reasonable rating

    const allRatings = [
      {
        Source: 'The Movie Database',
        Value: `${validVoteAverage.toFixed(1)}/10`
      }
    ];

    // Create the converted movie with guaranteed non-empty values
    const convertedMovie = {
      ...tmdbMovie,
      // Standard compatibility fields - clean values with meaningful fallbacks
      Title: title,
      Year: year,
      year: year,
      Rated: tmdbMovie.adult ? 'R' : 'PG-13', // Approximate
      Released: formatFirebaseDate(tmdbMovie.release_date, 'Release date unknown', { year: 'numeric', month: 'long', day: 'numeric' }),
      // Only include Runtime if we have actual runtime data
      ...(runtime && { Runtime: runtime }),
      Genre: genres,
      Director: director,
      Writer: writer,
      Actors: actors,
      Plot: tmdbMovie.overview && tmdbMovie.overview.trim() 
        ? tmdbMovie.overview.trim() 
        : 'Plot summary not available.',
      Language: languages,
      Country: countries,
      Awards: 'Not Available', // TMDb doesn't provide awards info
      Poster: tmdbMovie.poster_path 
        ? `${TMDB_IMAGE_BASE_URL}${tmdbMovie.poster_path}` 
        : 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjQ1MCIgdmlld0JveD0iMCAwIDMwMCA0NTAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iNDUwIiBmaWxsPSIjMzMzIi8+Cjx0ZXh0IHg9IjE1MCIgeT0iMjI1IiBmaWxsPSIjZmZmIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIHRleHQtYW5jaG9yPSJtaWRkbGUiPk5vIEltYWdlPC90ZXh0Pgo8L3N2Zz4K',
      Ratings: allRatings,
      Metascore: 'Not Available', // TMDb doesn't provide Metacritic scores directly
      imdbRating: validVoteAverage.toFixed(1), // Convert to IMDB-like format
      imdbVotes: (tmdbMovie.vote_count || 0).toLocaleString(),
      Type: 'movie',
      DVD: 'Not Available',
      BoxOffice: 'Not Available',
      Production: tmdbMovie.production_companies && tmdbMovie.production_companies.length > 0 
        ? tmdbMovie.production_companies[0].name 
        : 'Unknown Production',
      Website: 'Not Available',
      Response: 'True',
      imdbID: tmdbMovie.imdb_id || `tmdb_${tmdbMovie.id}` // Use consistent tmdb_ prefix format
    };

    console.log(`✅ Converted movie result:`, {
      Title: convertedMovie.Title,
      Year: convertedMovie.Year,
      Genre: convertedMovie.Genre,
      Runtime: convertedMovie.Runtime || 'Not provided',
      original_data: {
        title: tmdbMovie.title,
        release_date: tmdbMovie.release_date,
        id: tmdbMovie.id
      }
    });
    
    return convertedMovie;
  }

  // Create fallback movie object
  private createFallbackMovie(id: string, title?: string): TMDbMovie {
    const displayTitle = title || formatMovieDisplayTitleFromId(id);
    return {
      id: 0,
      title: displayTitle,
      original_title: displayTitle,
      release_date: '',
      year: 'N/A',
      overview: 'Movie details unavailable. Please check your TMDb API key configuration.',
      poster_path: null,
      backdrop_path: null,
      genre_ids: [],
      adult: false,
      video: false, // Added missing video property for shared compatibility
      original_language: 'en',
      popularity: 0,
      vote_average: 0,
      vote_count: 0,
      // Standard compatibility
      Title: displayTitle,
      Year: 'N/A',
      Rated: 'N/A',
      Released: 'N/A',
      Runtime: 'N/A',
      Genre: 'N/A',
      Director: 'N/A',
      Writer: 'N/A',
      Actors: 'N/A',
      Plot: 'Movie details unavailable. Please check your TMDb API key configuration.',
      Language: 'N/A',
      Country: 'N/A',
      Awards: 'N/A',
      Poster: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjQ1MCIgdmlld0JveD0iMCAwIDMwMCA0NTAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iNDUwIiBmaWxsPSIjMzMzIi8+Cjx0ZXh0IHg9IjE1MCIgeT0iMjI1IiBmaWxsPSIjZmZmIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIHRleHQtYW5jaG9yPSJtaWRkbGUiPk5vIEltYWdlPC90ZXh0Pgo8L3N2Zz4K',
      Ratings: [],
      Metascore: 'N/A',
      imdbRating: 'N/A',
      imdbVotes: 'N/A',
      Type: 'movie',
      DVD: 'N/A',
      BoxOffice: 'N/A',
      Production: 'N/A',
      Website: 'N/A',
      Response: 'True',
      imdbID: id
    };
  }

  async searchMovies(query: string, page: number = 1, includeCredits: boolean = false): Promise<{ Search: TMDbMovie[]; totalResults: string; Response: string }> {
    try {
      console.log(`🔍 TMDbService.searchMovies called with query: "${query}"`);
      
      // Use Firebase Functions if available
      if (shouldUseFunctionsAPI()) {
        console.log('🚀 Using Firebase Functions API for movie search');
        try {
          const result = await tmdbFunctionsService.searchMovies(query, page, includeCredits);
          console.log('✅ Functions API search result:', result);
          return result;
        } catch (functionsError) {
          console.warn('⚠️ Functions API failed for search, falling back to direct TMDB:', functionsError);
          // Fall through to direct API as fallback
        }
      } else {
        console.log('📡 Using direct TMDB API for movie search');
      }

      // Fallback to direct API calls
      const isValid = await this.validateApiKey();
      if (!isValid) {
        console.error('❌ TMDB API key is invalid');
        return {
          Search: [],
          totalResults: '0',
          Response: 'False'
        };
      }

      const response = await fetch(
        `${TMDB_BASE_URL}/search/movie?api_key=${this.apiKey}&query=${encodeURIComponent(query)}&page=${page}&include_adult=false`
      );
      
      if (response.status === 401) {
        this.apiKeyValid = false;
        console.warn('TMDb API key is invalid. Search functionality disabled.');
        return {
          Search: [],
          totalResults: '0',
          Response: 'False'
        };
      }
      
      if (!response.ok) {
        throw new Error(`Failed to fetch movies: ${response.status} ${response.statusText}`);
      }
      
      const data: TMDbSearchResult = await response.json();
      console.log(`📊 Raw TMDB search data for "${query}":`, data.results?.slice(0, 3));
      
      // Convert to standard format and ensure all movies have string IDs
      const convertedMovies = await Promise.all(data.results.slice(0, 10).map(async (movie, index) => {
        try {
          console.log(`🔄 Converting movie ${index + 1}:`, { 
            title: movie.title, 
            id: movie.id, 
            release_date: movie.release_date 
          });
          
          if (includeCredits) {
            try {
              // Fetch full movie details with credits for more complete information
              const fullMovieDetails = await this.getMovieById(movie.id.toString());
              console.log(`✅ Full details for ${movie.title}:`, {
                Title: fullMovieDetails.Title,
                Year: fullMovieDetails.Year,
                Genre: fullMovieDetails.Genre,
                Runtime: fullMovieDetails.Runtime
              });
              return fullMovieDetails;
            } catch (error) {
              console.warn(`Failed to fetch full details for movie ${movie.id}, using basic info`, error);
              // Fallback to basic conversion without credits
              const converted = await this.convertToStandardFormat(movie);
              converted.id = movie.id;
              console.log(`⚠️ Basic conversion for ${movie.title}:`, {
                Title: converted.Title,
                Year: converted.Year,
                Genre: converted.Genre,
                Runtime: converted.Runtime
              });
              return converted;
            }
          } else {
            // Basic conversion without additional API calls
            const converted = await this.convertToStandardFormat(movie);
            converted.id = movie.id;
            console.log(`✅ Basic conversion for ${movie.title}:`, {
              Title: converted.Title,
              Year: converted.Year,
              Genre: converted.Genre,
              Runtime: converted.Runtime,
              original_release_date: movie.release_date
            });
            return converted;
          }
        } catch (conversionError) {
          console.error(`❌ Failed to convert movie ${movie.title}:`, conversionError);
          // Return a fallback movie to prevent complete failure
          return this.createFallbackMovie(movie.id.toString(), movie.title);
        }
      }));
      
      // Filter out any failed conversions
      const validMovies = convertedMovies.filter(movie => movie !== null);
      console.log(`✅ Direct TMDB search completed: ${validMovies.length} movies converted successfully`);
      console.log('📋 Final converted movies sample:', validMovies.slice(0, 2).map(m => ({
        Title: m.Title,
        Year: m.Year,
        Genre: m.Genre,
        Runtime: m.Runtime
      })));
      
      return {
        Search: validMovies,
        totalResults: data.total_results.toString(),
        Response: 'True'
      };
    } catch (error) {
      console.error('💥 Movie search failed completely:', error);
      return {
        Search: [],
        totalResults: '0',
        Response: 'False'
      };
    }
  }

  /**
   * Enhanced search with natural language parsing and multi-field support
   * Supports queries like:
   * - "Inception 2010"
   * - "Leonardo DiCaprio movies"
   * - "Christopher Nolan director"
   * - "Action movies 2020"
   * - "Tom Hanks drama movies"
   */
  /**
   * Fast-path check: Determine if query is a simple title search
   * Simple queries skip heavy analysis and go straight to TMDB search
   */
  private isSimpleTitleQuery(query: string): boolean {
    const trimmed = query.trim().toLowerCase();

    // Too short - let it go through normal path
    if (trimmed.length < 2) return false;

    // Check for complex query patterns that need full analysis
    const complexPatterns = [
      /\b(movies?|films?)\s+(by|with|like|similar|starring|featuring|directed)/i,
      /^(best|top|new|recent|latest|oscar|award)\s+/i,
      /\s+(movies?|films?)$/i,
      /\b(malayalam|hindi|tamil|telugu|korean|japanese|french|spanish|german|italian|chinese)\b/i,
      /\b(action|comedy|drama|horror|thriller|romance|sci-fi|fantasy|documentary)\s+(movies?|films?)/i,
      /\b(19|20)\d{2}\b/, // Year patterns
      /\bactor\b|\bdirector\b|\bgenre\b/i,
      /\b(?:actor|cast|director|dir|year|yr):/i, // Inline keyword hints for enhanced search
    ];

    for (const pattern of complexPatterns) {
      if (pattern.test(trimmed)) {
        return false;
      }
    }

    const words = trimmed.split(/\s+/);
    // Two-word queries are often "Title Actor" or "Title language" — use full enhanced search, not fast TMDB title-only
    if (words.length === 2 && words.every(w => w.length >= 2)) {
      return false;
    }

    // Three+ tokens: e.g. "Season Mohan Lal" — basic TMDB search uses the whole string as a title and misses;
    // Firebase enhancedSearchMovies must run (title + person credits, etc.)
    if (words.length >= 3) {
      return false;
    }

    // Single-word titles only (two-word case handled above)
    return words.length === 1;
  }

  async enhancedSearchMovies(
    query: string,
    page: number = 1,
    opts?: { actor?: string; director?: string; year?: string }
  ): Promise<EnhancedSearchResponse> {
    const trimmedQuery = query.trim();
    const hasKeywordOpts = Boolean(
      opts?.actor?.trim() || opts?.director?.trim() || opts?.year?.trim()
    );

    // FAST PATH: For simple title queries, skip heavy analysis and go straight to TMDB
    if (!hasKeywordOpts && this.isSimpleTitleQuery(trimmedQuery)) {
      console.log('⚡ Fast path: Simple title search for:', trimmedQuery);

      // Check cache first
      const cacheKey = `fast_${trimmedQuery.toLowerCase()}_${page}`;
      const cached = this.searchCache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < this.cacheExpiryTime) {
        console.log('🎯 Fast path cache hit for:', trimmedQuery);
        return cached.results;
      }

      try {
        // Direct search - no heavy analysis needed
        const directSearch = await this.searchMovies(trimmedQuery, page, false);

        if (directSearch.Response === 'True' && directSearch.Search.length > 0) {
          const results: EnhancedSearchResult[] = directSearch.Search.map((movie, index) => ({
            movie,
            relevanceScore: 1.0 - (index * 0.02), // Slight decay for position
            matchedFields: ['title']
          }));

          const response: EnhancedSearchResponse = {
            Search: results,
            totalResults: directSearch.totalResults,
            Response: 'True',
            hasMore: parseInt(directSearch.totalResults) > results.length,
            actualTotal: parseInt(directSearch.totalResults),
            currentPage: page,
            totalPages: Math.ceil(parseInt(directSearch.totalResults) / 20)
          };

          // Cache the fast result
          this.searchCache.set(cacheKey, {
            results: response,
            timestamp: Date.now()
          });

          console.log(`⚡ Fast path returned ${results.length} results in minimal time`);
          return response;
        }
      } catch (error) {
        console.warn('⚠️ Fast path failed, falling back to full search:', error);
        // Fall through to full search
      }
    }

    // Use Firebase Functions if available
    if (shouldUseFunctionsAPI()) {
      console.log('🚀 Using Firebase Functions API for enhanced search');
      try {
        const functionsResult = await tmdbFunctionsService.enhancedSearchMovies(query, page, opts);
        const baseResults: EnhancedSearchResult[] = Array.isArray(functionsResult?.Search)
          ? functionsResult.Search.filter((entry: any) => entry && entry.movie)
          : [];
        // Skip directMatches when using Functions API - it already includes them
        const directMatches: EnhancedSearchResult[] = [];
        const response = this.buildEnhancedSearchResponse([...directMatches, ...baseResults], page, query);
        return {
          ...functionsResult,
          ...response,
          Search: response.Search,
          totalResults: response.totalResults,
          actualTotal: Math.max(
            response.actualTotal ?? 0,
            (functionsResult as any)?.actualTotal ?? 0,
            parseInt((functionsResult as any)?.totalResults ?? '0', 10) || 0
          ),
          hasMore: typeof (functionsResult as any)?.hasMore === 'boolean'
            ? (functionsResult as any).hasMore
            : response.hasMore,
          currentPage: response.currentPage,
          totalPages: response.totalPages
        };
      } catch (functionsError) {
        console.warn('⚠️ Functions API failed for enhanced search, falling back to direct TMDB:', functionsError);
        // Fall through to direct API as fallback
      }
    }

    // Fallback to direct API enhanced search
    try {
      const isValid = await this.validateApiKey();
      if (!isValid) {
        return {
          Search: [],
          totalResults: '0',
          Response: 'False'
        };
      }

      // Check cache first
      const cacheKey = `enhanced_${query.toLowerCase()}_${page}`;
      const cached = this.searchCache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < this.cacheExpiryTime) {
        console.log('🎯 Enhanced search cache hit for:', query);
        return cached.results;
      }

      // Track search analytics
      const normalizedQuery = query.toLowerCase().trim();
      const analytics = this.searchAnalytics.get(normalizedQuery) || { count: 0, lastSearched: 0 };
      analytics.count++;
      analytics.lastSearched = Date.now();
      this.searchAnalytics.set(normalizedQuery, analytics);

      // Enhanced intelligent query analysis
      const baseAnalysis = await this.analyzeQuery(query);
      const queryAnalysis = QueryProcessor.enhanceQueryAnalysis(baseAnalysis, query);
      console.log('🧠 Enhanced query analysis:', queryAnalysis);

      let searchResults: EnhancedSearchResult[] = [];

      // Multi-strategy search based on query analysis
      const searchStrategies = this.determineSearchStrategies(queryAnalysis);
      console.log('🎯 Search strategies:', searchStrategies);

      // Execute enhanced search strategies in parallel for better performance
      const strategyPromises = searchStrategies.map(async (strategy) => {
        switch (strategy.type) {
          // Enhanced multi-language strategies
          case 'title_with_language':
            return this.searchMoviesByTitleAndLanguage(strategy.value, strategy.language!, strategy.priority);
          case 'combined_term':
            return this.searchByCombinedTerms(strategy.value, strategy.priority);
          case 'multi_keyword':
            return this.searchByMultipleKeywords(strategy.value, strategy.priority);
          
          // Existing strategies
          case 'actor':
            return this.searchByActor(strategy.value, queryAnalysis.genre, strategy.priority, queryAnalysis.language);
          case 'director':
            return this.searchByDirector(strategy.value, queryAnalysis.genre, strategy.priority, queryAnalysis.language);
          case 'title':
            return this.searchByTitle(strategy.value, queryAnalysis.year, strategy.priority, queryAnalysis.language);
          case 'genre':
            return this.searchByGenre(strategy.value, queryAnalysis.year, strategy.priority, queryAnalysis.language);
          case 'production':
            return this.searchByProductionCompany(strategy.value, strategy.priority, queryAnalysis.language);
          case 'language':
            return this.searchByLanguage(strategy.value, queryAnalysis.genre, strategy.priority);
          case 'keyword':
            return this.searchByKeyword(strategy.value, strategy.priority, queryAnalysis.language);
          default:
            return [];
        }
      });

      // Wait for all strategies to complete
      const strategyResults = await Promise.all(strategyPromises);
      searchResults = strategyResults.flat();

      // Always include direct TMDB search results for the full query so exact title matches surface
      const directMatches = await this.getDirectTitleMatches(query, page);
      if (directMatches.length > 0) {
        searchResults.unshift(...directMatches);
      }

      // If no specific strategies found, do a comprehensive search
      if (searchResults.length === 0) {
        console.log('🔄 No specific matches, doing comprehensive search');
        const comprehensiveResults = await this.comprehensiveSearch(query, baseAnalysis);
        searchResults.push(...comprehensiveResults);
      }

      const result = this.buildEnhancedSearchResponse(searchResults, page, query);

      // Cache the result
      this.searchCache.set(cacheKey, {
        results: result,
        timestamp: Date.now()
      });

      // Clean up old cache entries (keep cache size manageable)
      if (this.searchCache.size > 100) {
        const oldestKey = Array.from(this.searchCache.keys())[0];
        this.searchCache.delete(oldestKey);
      }

      return result;
    } catch (error) {
      console.error('Error in enhanced search:', error);
      // Fallback to regular search
      const fallbackResults = await this.searchMovies(query, page, false);
      return {
        Search: fallbackResults.Search.map(movie => ({
          movie,
          relevanceScore: 1.0,
          matchedFields: ['title']
        })),
        totalResults: fallbackResults.totalResults,
        Response: fallbackResults.Response
      };
    }
  }

  /**
   * Get search suggestions based on partial query
   */
  async getSearchSuggestions(query: string): Promise<string[]> {
    if (!query || query.length < 2) return [];

    try {
      const suggestions: string[] = [];
      
      // Get movie title suggestions
      const movieResponse = await fetch(
        `${TMDB_BASE_URL}/search/movie?api_key=${this.apiKey}&query=${encodeURIComponent(query)}&include_adult=false&page=1`
      );
      
      if (movieResponse.ok) {
        const movieData = await movieResponse.json();
        movieData.results.slice(0, 5).forEach((movie: any) => {
          if (movie.title && !suggestions.includes(movie.title)) {
            suggestions.push(movie.title);
          }
        });
      }

      // Get person suggestions
      const personResponse = await fetch(
        `${TMDB_BASE_URL}/search/person?api_key=${this.apiKey}&query=${encodeURIComponent(query)}&include_adult=false&page=1`
      );
      
      if (personResponse.ok) {
        const personData = await personResponse.json();
        personData.results.slice(0, 3).forEach((person: any) => {
          if (person.name && !suggestions.includes(person.name)) {
            suggestions.push(person.name);
          }
        });
      }

      // Add genre suggestions if query matches
      const genres = [
        'action', 'adventure', 'animation', 'comedy', 'crime', 'documentary',
        'drama', 'family', 'fantasy', 'history', 'horror', 'music', 'mystery',
        'romance', 'science fiction', 'thriller', 'war', 'western'
      ];
      
      const lowerQuery = query.toLowerCase();
      genres.forEach(genre => {
        if (genre.toLowerCase().startsWith(lowerQuery) && !suggestions.includes(genre)) {
          suggestions.push(genre);
        }
      });

      return suggestions.slice(0, 8); // Limit to 8 suggestions
    } catch (error) {
      console.error('Error getting search suggestions:', error);
      return [];
    }
  }

  /**
   * Determine search strategies based on enhanced query analysis
   */
  private determineSearchStrategies(analysis: EnhancedQueryAnalysis): SearchStrategy[] {
    const strategies: SearchStrategy[] = [];

    // Enhanced multi-language strategies
    if (analysis.isLanguageSpecific && analysis.movieTitle) {
      // Primary strategy: movie title with specific languages
      analysis.languages.forEach(langCode => {
        strategies.push({
          type: 'title_with_language',
          value: analysis.movieTitle!,
          language: langCode,
          priority: 10,
          confidence: analysis.confidence * 0.9
        });
      });
      
      // Secondary strategy: exact combined search
      analysis.combinedTerms.forEach(term => {
        strategies.push({
          type: 'combined_term',
          value: term,
          priority: 9,
          confidence: analysis.confidence * 0.8
        });
      });
    }

    const normalizedTitle = analysis.title?.trim();
    const combinedTermsValue = analysis.combinedTerms.join(' ').trim();
    const multiKeywordValue = normalizedTitle && normalizedTitle.length > 0
      ? normalizedTitle
      : combinedTermsValue;

    // Multi-keyword search strategies (use full title when available)
    if (multiKeywordValue.split(/\s+/).length > 1) {
      strategies.push({
        type: 'multi_keyword',
        value: multiKeywordValue,
        priority: 9,
        confidence: analysis.confidence * 0.85
      });
    }

    // Ensure we issue an exact combined search when title exists
    if (normalizedTitle && normalizedTitle.length > 0) {
      strategies.push({
        type: 'combined_term',
        value: normalizedTitle,
        priority: 8,
        confidence: analysis.confidence * 0.8
      });
    } else if (combinedTermsValue.length > 0) {
      strategies.push({
        type: 'combined_term',
        value: combinedTermsValue,
        priority: 7,
        confidence: analysis.confidence * 0.7
      });
    }

    // Actor-based strategies
    analysis.actors.forEach(actor => {
      strategies.push({
        type: 'actor',
        value: actor,
        priority: analysis.queryType === 'person-focused' ? 10 : 8,
        confidence: analysis.confidence
      });
    });

    // Director-based strategies
    analysis.directors.forEach(director => {
      strategies.push({
        type: 'director',
        value: director,
        priority: analysis.queryType === 'person-focused' ? 10 : 8,
        confidence: analysis.confidence
      });
    });

    // Title-based strategies
    if (analysis.title) {
      strategies.push({
        type: 'title',
        value: analysis.title,
        priority: analysis.queryType === 'title-focused' ? 10 : 7,
        confidence: analysis.confidence
      });
    }

    // Genre-based strategies
    if (analysis.genre) {
      strategies.push({
        type: 'genre',
        value: analysis.genre,
        priority: analysis.queryType === 'genre-focused' ? 9 : 6,
        confidence: analysis.confidence * 0.8
      });
    }

    // Production company strategies
    if (analysis.productionCompany) {
      strategies.push({
        type: 'production',
        value: analysis.productionCompany,
        priority: analysis.queryType === 'production-focused' ? 9 : 6,
        confidence: analysis.confidence * 0.7
      });
    }

    // Language strategies
    if (analysis.language) {
      strategies.push({
        type: 'language',
        value: analysis.language,
        priority: analysis.queryType === 'language-focused' ? 8 : 6,
        confidence: analysis.confidence * 0.8
      });
    }

    // Keyword strategies (for comprehensive search)
    analysis.keywords.forEach(keyword => {
      if (keyword.length > 3) { // Only meaningful keywords
        strategies.push({
          type: 'keyword',
          value: keyword,
          priority: 4,
          confidence: 0.5
        });
      }
    });

    // Sort by priority and confidence
    return strategies.sort((a, b) => {
      const scoreA = a.priority * a.confidence;
      const scoreB = b.priority * b.confidence;
      return scoreB - scoreA;
    });
  }

  /**
   * Search by production company
   */
  private async searchByProductionCompany(company: string, priority: number = 5, language?: string): Promise<EnhancedSearchResult[]> {
    try {
      // First, find the production company ID
      const companyResponse = await fetch(
        `${TMDB_BASE_URL}/search/company?api_key=${this.apiKey}&query=${encodeURIComponent(company)}`
      );

      if (!companyResponse.ok) return [];

      const companyData = await companyResponse.json();
      if (companyData.results.length === 0) return [];

      const companyId = companyData.results[0].id;

      // Search for movies by this production company with language filtering
      const languageParam = language ? `&with_original_language=${this.getLanguageCode(language)}` : '';
      const moviesResponse = await fetch(
        `${TMDB_BASE_URL}/discover/movie?api_key=${this.apiKey}&with_companies=${companyId}&sort_by=popularity.desc${languageParam}&include_adult=false`
      );

      if (!moviesResponse.ok) return [];

      const moviesData = await moviesResponse.json();

      // Convert movies to standard format
      const convertedMovies = await Promise.all(
        moviesData.results.slice(0, 15).map(async (movie: any) => {
          const convertedMovie = await this.convertToStandardFormat(movie);
          return {
            movie: convertedMovie,
            relevanceScore: this.calculateProductionRelevance(movie, company, priority),
            matchedFields: ['production']
          };
        })
      );

      return convertedMovies;
    } catch (error) {
      console.error('Error searching by production company:', error);
      return [];
    }
  }

  /**
   * Search movies by language
   */
  private async searchByLanguage(languageCode: string, genre?: string, priority: number = 6): Promise<EnhancedSearchResult[]> {
    try {
      console.log(`🌍 Searching movies in language: ${languageCode}, genre: ${genre}`);
      
      let discoverUrl = `${TMDB_BASE_URL}/discover/movie?api_key=${this.apiKey}&with_original_language=${languageCode}&sort_by=popularity.desc&include_adult=false&vote_count.gte=10`;
      
      // Add genre filter if specified
      if (genre) {
        // Map genre name to ID
        const genreId = await this.getGenreIdByName(genre);
        if (genreId) {
          discoverUrl += `&with_genres=${genreId}`;
        }
      }

      const response = await fetch(discoverUrl);
      if (!response.ok) return [];

      const data = await response.json();
      
      // Get additional pages for more variety
      const allMovies = [...data.results];
      if (data.total_pages > 1 && allMovies.length < 20) {
        const page2Response = await fetch(`${discoverUrl}&page=2`);
        if (page2Response.ok) {
          const page2Data = await page2Response.json();
          allMovies.push(...page2Data.results);
        }
      }

      // Convert to standard format
      const convertedMovies = await Promise.all(
        allMovies.slice(0, 25).map(async (movie: any) => {
          const convertedMovie = await this.convertToStandardFormat(movie);
          return {
            movie: convertedMovie,
            relevanceScore: this.calculateLanguageRelevance(movie, languageCode, genre, priority),
            matchedFields: ['language', 'original_language']
          };
        })
      );

      console.log(`🌍 Found ${convertedMovies.length} movies in language ${languageCode}`);
      return convertedMovies;
    } catch (error) {
      console.error('Error searching by language:', error);
      return [];
    }
  }

  /**
   * Helper method to get genre ID by name
   */
  private async getGenreIdByName(genreName: string): Promise<number | null> {
    try {
      const genres = await this.getGenres();
      const genre = genres.find(g => g.name.toLowerCase() === genreName.toLowerCase());
      return genre ? genre.id : null;
    } catch (error) {
      console.error('Error getting genre ID:', error);
      return null;
    }
  }

  /**
   * Calculate relevance score for language matches
   */
  private calculateLanguageRelevance(movie: any, languageCode: string, genre?: string, priority: number = 6): number {
    let score = 0.6; // Base score for language match

    // Higher score for more popular movies
    if (movie.popularity > 10) score += 0.1;
    if (movie.popularity > 50) score += 0.1;

    // Genre match bonus
    if (genre && movie.genre_ids) {
      // Simplified genre matching - in production, would need proper genre ID mapping
      score += 0.15;
    }

    // Higher score for better ratings
    if (movie.vote_average > 7) {
      score += 0.1;
    } else if (movie.vote_average > 6) {
      score += 0.05;
    }

    // Priority boost
    score += (priority / 10) * 0.1;

    return Math.min(score, 1.0);
  }

  /**
   * Enhanced keyword search using TMDB's discover endpoint and keyword database
   */
  private async searchByKeyword(keyword: string, priority: number = 4, language?: string): Promise<EnhancedSearchResult[]> {
    try {
      const results: EnhancedSearchResult[] = [];

      // First try to find TMDB keyword ID for more precise results
      const keywordResponse = await fetch(
        `${TMDB_BASE_URL}/search/keyword?api_key=${this.apiKey}&query=${encodeURIComponent(keyword)}`
      );

      let keywordBasedResults: EnhancedSearchResult[] = [];
      if (keywordResponse.ok) {
        const keywordData = await keywordResponse.json();
        if (keywordData.results.length > 0) {
          const tmdbKeyword = keywordData.results[0];
          
          // Use discover endpoint with keyword ID for more accurate results
          const languageParam = language ? `&with_original_language=${this.getLanguageCode(language)}` : '';
          const discoverResponse = await fetch(
            `${TMDB_BASE_URL}/discover/movie?api_key=${this.apiKey}&with_keywords=${tmdbKeyword.id}&sort_by=popularity.desc${languageParam}&include_adult=false`
          );
          
          if (discoverResponse.ok) {
            const discoverData = await discoverResponse.json();
            
            keywordBasedResults = await Promise.all(
              discoverData.results.slice(0, 10).map(async (movie: any) => {
                const convertedMovie = await this.convertToStandardFormat(movie);
                return {
                  movie: convertedMovie,
                  relevanceScore: this.calculateKeywordRelevance(movie, keyword, priority) + 0.2, // Boost for exact keyword match
                  matchedFields: ['keyword', 'plot']
                };
              })
            );
          }
        }
      }

      // Fallback to general movie search if keyword search yields few results
      if (keywordBasedResults.length < 5) {
        const movieSearchResponse = await fetch(
          `${TMDB_BASE_URL}/search/movie?api_key=${this.apiKey}&query=${encodeURIComponent(keyword)}&include_adult=false`
        );

        if (movieSearchResponse.ok) {
          const movieData = await movieSearchResponse.json();
          
          const movieBasedResults = await Promise.all(
            movieData.results.slice(0, 15 - keywordBasedResults.length).map(async (movie: any) => {
              const convertedMovie = await this.convertToStandardFormat(movie);
              return {
                movie: convertedMovie,
                relevanceScore: this.calculateKeywordRelevance(movie, keyword, priority),
                matchedFields: ['title', 'overview']
              };
            })
          );

          results.push(...movieBasedResults);
        }
      }

      // Combine and deduplicate results
      const allResults = [...keywordBasedResults, ...results];
      return this.deduplicateAndScore(allResults);
    } catch (error) {
      console.error('Error searching by keyword:', error);
      return [];
    }
  }

  /**
   * Enhanced method: Search movies by title and language combination
   * Example: "Moonwalkers" with language "ml" (Malayalam)
   */
  private async searchMoviesByTitleAndLanguage(title: string, languageCode: string, priority: number = 10): Promise<EnhancedSearchResult[]> {
    try {
      console.log(`🎬 Searching "${title}" in language: ${languageCode}`);

      // Generate possible transliterations for the title
      const searchVariants = this.generateTitleVariants(title, languageCode);
      console.log(`🔤 Search variants for "${title}":`, searchVariants);

      let results: EnhancedSearchResult[] = [];

      // Strategy 1: Search with all title variants
      for (const variant of searchVariants) {
        const titleWithLanguageUrl = `${TMDB_BASE_URL}/search/movie?api_key=${this.apiKey}&query=${encodeURIComponent(variant)}&with_original_language=${languageCode}&include_adult=false`;
        const titleResponse = await fetch(titleWithLanguageUrl);

        if (titleResponse.ok) {
          const titleData = await titleResponse.json();
          const titleResults = await Promise.all(
            titleData.results.slice(0, 10).map(async (movie: any) => {
              const convertedMovie = await this.convertToStandardFormat(movie);
              return {
                movie: convertedMovie,
                relevanceScore: this.calculateTitleLanguageRelevance(movie, title, languageCode, priority),
                matchedFields: ['title', 'language', 'variant:' + variant]
              };
            })
          );
          results.push(...titleResults);
        }
      }
      
      // Strategy 2: Discover movies by language, then filter by title similarity
      const discoverUrl = `${TMDB_BASE_URL}/discover/movie?api_key=${this.apiKey}&with_original_language=${languageCode}&sort_by=popularity.desc&include_adult=false&vote_count.gte=10`;
      const discoverResponse = await fetch(discoverUrl);
      
      if (discoverResponse.ok) {
        const discoverData = await discoverResponse.json();
        const languageResults = await Promise.all(
          discoverData.results.slice(0, 20).map(async (movie: any) => {
            if (this.titleSimilarity(movie.title, title) > 0.3 || 
                this.titleSimilarity(movie.original_title, title) > 0.3) {
              const convertedMovie = await this.convertToStandardFormat(movie);
              return {
                movie: convertedMovie,
                relevanceScore: this.calculateTitleLanguageRelevance(movie, title, languageCode, priority * 0.8),
                matchedFields: ['language', 'title_similarity']
              };
            }
            return null;
          })
        );
        
        const validLanguageResults = languageResults.filter(result => result !== null) as EnhancedSearchResult[];
        results.push(...validLanguageResults);
      }
      
      return this.deduplicateAndScore(results);
    } catch (error) {
      console.error('Error in title+language search:', error);
      return [];
    }
  }

  /**
   * Generate possible search variants for a title in different scripts/transliterations
   */
  private generateTitleVariants(title: string, languageCode: string): string[] {
    const variants = [title]; // Start with original

    // Basic transliteration mappings for common Indian languages
    const transliterations: { [key: string]: { [key: string]: string } } = {
      'ml': { // Malayalam
        'guru': 'ഗുരു',
        'movie': 'സിനിമ',
        'film': 'ചലച്ചിത്രം'
      },
      'hi': { // Hindi
        'guru': 'गुरु',
        'movie': 'फिल्म',
        'film': 'फिल्म'
      },
      'ta': { // Tamil
        'guru': 'குரு',
        'movie': 'படம்',
        'film': 'திரைப்படம்'
      },
      'te': { // Telugu
        'guru': 'గురువు',
        'movie': 'సినిమా',
        'film': 'చిత్రం'
      },
      'kn': { // Kannada
        'guru': 'ಗುರು',
        'movie': 'ಸಿನಿಮಾ',
        'film': 'ಚಿತ್ರ'
      }
    };

    // Add transliterated version if available
    const langTransliterations = transliterations[languageCode];
    if (langTransliterations) {
      const lowerTitle = title.toLowerCase();
      for (const [english, native] of Object.entries(langTransliterations)) {
        if (lowerTitle.includes(english)) {
          const transliterated = title.toLowerCase().replace(english, native);
          variants.push(transliterated);
          // Also try with proper case
          variants.push(native);
        }
      }
    }

    // Remove duplicates and return
    return [...new Set(variants)];
  }

  /**
   * Enhanced method: Search by combined terms
   * Handles complex queries like "Moonwalkers Malayalam" as a single search
   */
  private async searchByCombinedTerms(combinedQuery: string, priority: number = 9): Promise<EnhancedSearchResult[]> {
    try {
      console.log(`🔍 Searching combined terms: "${combinedQuery}"`);
      
      // Direct search with combined terms
      const searchUrl = `${TMDB_BASE_URL}/search/movie?api_key=${this.apiKey}&query=${encodeURIComponent(combinedQuery)}&include_adult=false`;
      const response = await fetch(searchUrl);
      
      if (!response.ok) return [];
      
      const data = await response.json();
      
      const results = await Promise.all(
        data.results.slice(0, 15).map(async (movie: any) => {
          const convertedMovie = await this.convertToStandardFormat(movie);
          return {
            movie: convertedMovie,
            relevanceScore: this.calculateCombinedTermsRelevance(movie, combinedQuery, priority),
            matchedFields: ['combined_search', 'title']
          };
        })
      );
      
      return results;
    } catch (error) {
      console.error('Error in combined terms search:', error);
      return [];
    }
  }
  
  /**
   * Enhanced method: Search by multiple keywords with intelligent weighting
   */
  private async searchByMultipleKeywords(keywords: string, priority: number = 8): Promise<EnhancedSearchResult[]> {
    try {
      console.log(`🔍 Multi-keyword search: "${keywords}"`);

      const stopWords = new Set(['the', 'and', 'for', 'with', 'from', 'into', 'of', 'to', 'in', 'on', 'at', 'by']);
      const rawTerms = keywords
        .split(' ')
        .map(term => term.trim())
        .filter(term => term.length > 2 && !stopWords.has(term.toLowerCase()));

      const uniqueTermMap = new Map<string, string>();
      rawTerms.forEach(term => {
        const normalized = term.toLowerCase();
        if (!uniqueTermMap.has(normalized)) {
          uniqueTermMap.set(normalized, term);
        }
      });

      const searchTerms = Array.from(uniqueTermMap.keys());
      if (searchTerms.length === 0) {
        return [];
      }

      const totalTerms = searchTerms.length;
      let allResults: EnhancedSearchResult[] = [];
      const movieScores = new Map<number, { result: EnhancedSearchResult; matchedTerms: Set<string>; phraseMatched: boolean }>();

      const fullPhrase = keywords.trim();
      if (fullPhrase.length > 0) {
        const phraseUrl = `${TMDB_BASE_URL}/search/movie?api_key=${this.apiKey}&query=${encodeURIComponent(fullPhrase)}&include_adult=false`;
        const phraseResponse = await fetch(phraseUrl);
        if (phraseResponse.ok) {
          const phraseData = await phraseResponse.json();
          const phraseResults = await Promise.all(
            phraseData.results.slice(0, 10).map(async (movie: any) => {
              const convertedMovie = await this.convertToStandardFormat(movie);
              const phraseScore = this.calculateMultiKeywordRelevance(movie, searchTerms, priority);
              return {
                movie: convertedMovie,
                relevanceScore: Math.min(0.99, phraseScore + 0.1),
                matchedFields: ['multi_keyword', 'multi_keyword_phrase', 'title']
              } as EnhancedSearchResult;
            })
          );

          phraseResults.forEach(result => {
            const movieId = result.movie.id;
            movieScores.set(movieId, {
              result: {
                ...result,
                matchedFields: Array.from(new Set(result.matchedFields))
              },
              matchedTerms: new Set(searchTerms),
              phraseMatched: true
            });
          });
        }
      }

      // Search for each term individually, then combine and weight results
      for (const term of searchTerms) {
        const originalTerm = uniqueTermMap.get(term) || term;
        const searchUrl = `${TMDB_BASE_URL}/search/movie?api_key=${this.apiKey}&query=${encodeURIComponent(originalTerm)}&include_adult=false`;
        const response = await fetch(searchUrl);
        
        if (response.ok) {
          const data = await response.json();
          
          const termResults = await Promise.all(
            data.results.slice(0, 10).map(async (movie: any) => {
              const convertedMovie = await this.convertToStandardFormat(movie);
              return {
                movie: convertedMovie,
                relevanceScore: this.calculateMultiKeywordRelevance(movie, searchTerms, priority),
                matchedFields: ['multi_keyword', 'title'],
                matchedTerm: term
              };
            })
          );
          
          allResults.push(...termResults);
        }
      }

      // Combine term-level results into aggregate scores
      allResults.forEach(result => {
        const movieId = result.movie.id;
        const matchedTerm = typeof (result as any).matchedTerm === 'string'
          ? (result as any).matchedTerm.toLowerCase()
          : null;

        if (movieScores.has(movieId)) {
          const existing = movieScores.get(movieId)!;
          if (matchedTerm) {
            existing.matchedTerms.add(matchedTerm);
          }
          existing.result.relevanceScore = Math.max(existing.result.relevanceScore, result.relevanceScore);
          const fieldSet = new Set([...existing.result.matchedFields, ...result.matchedFields]);
          existing.result.matchedFields = Array.from(fieldSet);
        } else {
          const fieldSet = new Set(result.matchedFields);
          const matchedTerms = new Set<string>();
          if (matchedTerm) {
            matchedTerms.add(matchedTerm);
          }
          movieScores.set(movieId, {
            result: {
              ...result,
              matchedFields: Array.from(fieldSet)
            },
            matchedTerms,
            phraseMatched: false
          });
        }
      });

      const aggregatedEntries = Array.from(movieScores.values()).map(({ result, matchedTerms, phraseMatched }) => {
        const matchCount = phraseMatched ? totalTerms : matchedTerms.size;
        const coverage = totalTerms > 0 ? Math.min(1, matchCount / totalTerms) : 0;
        const matchTag = `matches_${Math.max(1, matchCount)}_terms`;
        const fieldSet = new Set(result.matchedFields);
        fieldSet.add(matchTag);
        if (phraseMatched) {
          fieldSet.add('multi_keyword_phrase');
        }
        const isStrong = coverage >= 0.75 || totalTerms === 1;
        fieldSet.add(isStrong ? 'multi_word_strong_match' : 'multi_word_partial_match');

        const boostedScore = isStrong ? result.relevanceScore + 0.08 : result.relevanceScore;
        const multiplier = 0.7 + coverage * 0.5; // 0.7 - 1.2 range
        const adjustedScore = Math.min(0.99, boostedScore * multiplier);

        const sanitizedResult: any = { ...result };
        delete sanitizedResult.matchedTerm;

        return {
          coverage,
          entry: {
            ...sanitizedResult,
            relevanceScore: adjustedScore,
            matchedFields: Array.from(fieldSet)
          }
        };
      });

      if (aggregatedEntries.length === 0) {
        return [];
      }

      return aggregatedEntries
        .sort((a, b) => {
          if (b.entry.relevanceScore !== a.entry.relevanceScore) {
            return b.entry.relevanceScore - a.entry.relevanceScore;
          }
          return b.coverage - a.coverage;
        })
        .map(({ entry }) => entry);
    } catch (error) {
      console.error('Error in multi-keyword search:', error);
      return [];
    }
  }

  /**
   * New method: Search by specific crew role (cinematographer, composer, etc.)
   */
  private async searchByCrewRole(personName: string, role: string, priority: number = 6): Promise<EnhancedSearchResult[]> {
    try {
      // Find the person first
      const personResponse = await fetch(
        `${TMDB_BASE_URL}/search/person?api_key=${this.apiKey}&query=${encodeURIComponent(personName)}&include_adult=false`
      );

      if (!personResponse.ok) return [];

      const personData = await personResponse.json();
      if (personData.results.length === 0) return [];

      const person = personData.results[0];
      
      // Get their movie credits
      const creditsResponse = await fetch(
        `${TMDB_BASE_URL}/person/${person.id}/movie_credits?api_key=${this.apiKey}`
      );

      if (!creditsResponse.ok) return [];

      const creditsData = await creditsResponse.json();
      
      // Filter for the specific role
      const roleMapping: { [key: string]: string[] } = {
        'cinematographer': ['Director of Photography', 'Cinematography'],
        'composer': ['Original Music Composer', 'Music', 'Music Score'],
        'editor': ['Editor', 'Editing'],
        'writer': ['Screenplay', 'Writer', 'Story', 'Adaptation'],
        'producer': ['Producer', 'Executive Producer', 'Co-Producer']
      };

      const targetJobs = roleMapping[role.toLowerCase()] || [role];
      const roleMovies = creditsData.crew.filter((credit: any) => 
        targetJobs.some(job => credit.job?.toLowerCase().includes(job.toLowerCase()))
      );

      // Convert movies to standard format
      const convertedMovies = await Promise.all(
        roleMovies.slice(0, 15).map(async (movie: any) => {
          const convertedMovie = await this.convertToStandardFormat(movie);
          return {
            movie: convertedMovie,
            relevanceScore: this.calculateProductionRelevance(movie, person.name, priority),
            matchedFields: [`crew_${role}`, 'crew']
          };
        })
      );
      
      return convertedMovies;
    } catch (error) {
      console.error(`Error searching by ${role}:`, error);
      return [];
    }
  }

  /**
   * Enhanced comprehensive search leveraging multiple TMDB endpoints
   */
  private async comprehensiveSearch(query: string, analysis: QueryAnalysis): Promise<EnhancedSearchResult[]> {
    try {
      const results: EnhancedSearchResult[] = [];

      // 1. Multi-search endpoint for broad discovery
      const multiResponse = await fetch(
        `${TMDB_BASE_URL}/search/multi?api_key=${this.apiKey}&query=${encodeURIComponent(query)}&include_adult=false`
      );

      if (multiResponse.ok) {
        const multiData = await multiResponse.json();
        
        // Process ONLY movie results from multi-search
        const movieResults = multiData.results.filter((item: any) => 
          item.media_type === 'movie' && item.title && item.title.trim().length > 0
        );
        
        if (movieResults.length > 0) {
          const convertedMovies = await Promise.all(
            movieResults.slice(0, 8).map(async (movie: any) => {
              try {
                const convertedMovie = await this.convertToStandardFormat(movie);
                return {
                  movie: convertedMovie,
                  relevanceScore: this.calculateComprehensiveRelevance(movie, query, analysis),
                  matchedFields: ['multi_search']
                };
              } catch (error) {
                console.warn('Failed to convert movie from multi-search:', movie.id, error);
                return null;
              }
            })
          );

          // Filter out failed conversions
          const validMovies = convertedMovies.filter(result => result !== null) as EnhancedSearchResult[];
          results.push(...validMovies);
        }

        // Process person results to find their movies (but only return movies)
        const personResults = multiData.results.filter((item: any) => item.media_type === 'person');
        for (const person of personResults.slice(0, 2)) {
          try {
            const personMovies = await this.getTopMoviesForPerson(person, 5);
            const personMovieResults = await Promise.all(
              personMovies.map(async (movie: any) => {
                try {
                  // Ensure this is a movie with a title
                  if (!movie.title || movie.title.trim().length === 0) {
                    return null;
                  }
                  const convertedMovie = await this.convertToStandardFormat(movie);
                  return {
                    movie: convertedMovie,
                    relevanceScore: this.calculateComprehensiveRelevance(movie, query, analysis) + 0.1, // Slight boost for person connection
                    matchedFields: ['person_connection', 'cast_crew']
                  };
                } catch (error) {
                  console.warn('Failed to convert person movie:', movie.id, error);
                  return null;
                }
              })
            );
            
            // Filter out failed conversions
            const validPersonMovies = personMovieResults.filter(result => result !== null) as EnhancedSearchResult[];
            results.push(...validPersonMovies);
          } catch (error) {
            console.warn('Failed to get movies for person:', person.id, error);
          }
        }
      }

      // 2. Enhanced discover search based on analysis
      if (analysis.genre || analysis.year || analysis.productionCompany) {
        try {
          const discoverResults = await this.enhancedDiscoverSearch(analysis);
          results.push(...discoverResults);
        } catch (error) {
          console.warn('Failed enhanced discover search:', error);
        }
      }

      // 3. Keyword-based discovery if we have meaningful keywords
      if (analysis.keywords.length > 0) {
        for (const keyword of analysis.keywords.slice(0, 2)) {
          try {
            const keywordResults = await this.searchByKeyword(keyword, 3);
            results.push(...keywordResults.slice(0, 3));
          } catch (error) {
            console.warn('Failed keyword search for:', keyword, error);
          }
        }
      }

      // Deduplicate and limit results - ensure all results have valid movies with titles
      const deduplicatedResults = this.deduplicateAndScore(results);
      const validResults = deduplicatedResults.filter(result => 
        result.movie && result.movie.title && result.movie.title.trim().length > 0
      );
      
      return validResults.slice(0, 20);
    } catch (error) {
      console.error('Error in comprehensive search:', error);
      return [];
    }
  }

  /**
   * Enhanced discover search using multiple filters
   */
  private async enhancedDiscoverSearch(analysis: QueryAnalysis): Promise<EnhancedSearchResult[]> {
    try {
      let discoverUrl = `${TMDB_BASE_URL}/discover/movie?api_key=${this.apiKey}&include_adult=false&sort_by=popularity.desc`;
      
      // Add filters based on analysis
      if (analysis.year) {
        discoverUrl += `&year=${analysis.year}`;
      }
      
      if (analysis.genre) {
        // Get genre ID first
        const genresResponse = await fetch(`${TMDB_BASE_URL}/genre/movie/list?api_key=${this.apiKey}`);
        if (genresResponse.ok) {
          const genresData = await genresResponse.json();
          const genre = genresData.genres.find((g: any) => 
            g.name.toLowerCase().includes(analysis.genre!.toLowerCase())
          );
          if (genre) {
            discoverUrl += `&with_genres=${genre.id}`;
          }
        }
      }

      // Add production company filter if available
      if (analysis.productionCompany) {
        const companyResponse = await fetch(
          `${TMDB_BASE_URL}/search/company?api_key=${this.apiKey}&query=${encodeURIComponent(analysis.productionCompany)}`
        );
        if (companyResponse.ok) {
          const companyData = await companyResponse.json();
          if (companyData.results.length > 0) {
            discoverUrl += `&with_companies=${companyData.results[0].id}`;
          }
        }
      }

      const discoverResponse = await fetch(discoverUrl);
      if (!discoverResponse.ok) return [];

      const discoverData = await discoverResponse.json();
      
      const convertedMovies = await Promise.all(
        discoverData.results.slice(0, 10).map(async (movie: any) => {
          const convertedMovie = await this.convertToStandardFormat(movie);
          return {
            movie: convertedMovie,
            relevanceScore: this.calculateComprehensiveRelevance(movie, analysis.originalQuery, analysis) + 0.15, // Boost for targeted discovery
            matchedFields: ['discover', 'filtered_search']
          };
        })
      );
      
      return convertedMovies;
    } catch (error) {
      console.error('Error in enhanced discover search:', error);
      return [];
    }
  }

  /**
   * Helper method to get top movies for a person (from multi-search results)
   */
  private async getTopMoviesForPerson(person: any, limit: number = 5): Promise<any[]> {
    try {
      const creditsResponse = await fetch(
        `${TMDB_BASE_URL}/person/${person.id}/movie_credits?api_key=${this.apiKey}`
      );
      
      if (!creditsResponse.ok) return [];
      
      const creditsData = await creditsResponse.json();
      
      // Combine cast and crew, prioritize by popularity and rating
      const allMovies = [
        ...creditsData.cast.map((m: any) => ({ ...m, role_type: 'cast' })),
        ...creditsData.crew.map((m: any) => ({ ...m, role_type: 'crew' }))
      ];
      
      // Remove duplicates and sort by popularity/rating
      const uniqueMovies = allMovies.reduce((acc: any[], movie: any) => {
        if (!acc.find(m => m.id === movie.id)) {
          acc.push(movie);
        }
        return acc;
      }, []);
      
      uniqueMovies.sort((a: any, b: any) => {
        const scoreA = (a.popularity || 0) * 0.6 + (a.vote_average || 0) * 0.4;
        const scoreB = (b.popularity || 0) * 0.6 + (b.vote_average || 0) * 0.4;
        return scoreB - scoreA;
      });
      
      return uniqueMovies.slice(0, limit);
    } catch (error) {
      console.error('Error getting person top movies:', error);
      return [];
    }
  }

  /**
   * Get popular search queries
   */
  getPopularSearches(limit: number = 10): Array<{ query: string; count: number; lastSearched: number }> {
    return Array.from(this.searchAnalytics.entries())
      .map(([query, data]) => ({ query, ...data }))
      .sort((a, b) => b.count - a.count)
      .slice(0, limit);
  }

  /**
   * Get search statistics
   */
  getSearchStats(): { totalSearches: number; uniqueQueries: number; cacheHitRate: number } {
    const totalSearches = Array.from(this.searchAnalytics.values()).reduce((sum, data) => sum + data.count, 0);
    const uniqueQueries = this.searchAnalytics.size;
    const cacheHitRate = this.searchCache.size > 0 ? (this.searchCache.size / totalSearches) * 100 : 0;
    
    return {
      totalSearches,
      uniqueQueries,
      cacheHitRate: Math.round(cacheHitRate * 100) / 100
    };
  }

  /**
   * Clear search cache and analytics
   */
  clearSearchData(): void {
    this.searchCache.clear();
    this.searchAnalytics.clear();
    console.log('🧹 Search cache and analytics cleared');
  }

  /**
   * Get language code from language name
   */
  private getLanguageCode(language: string): string {
    const languageMapping: { [key: string]: string } = {
      'english': 'en',
      'hindi': 'hi',
      'bollywood': 'hi',
      'spanish': 'es',
      'french': 'fr',
      'italian': 'it',
      'german': 'de',
      'japanese': 'ja',
      'korean': 'ko',
      'k-movies': 'ko',
      'chinese': 'zh',
      'mandarin': 'zh',
      'cantonese': 'zh',
      'portuguese': 'pt',
      'russian': 'ru',
      'arabic': 'ar',
      'dutch': 'nl',
      'swedish': 'sv',
      'norwegian': 'no',
      'danish': 'da',
      'finnish': 'fi',
      'polish': 'pl',
      'czech': 'cs',
      'hungarian': 'hu',
      'greek': 'el',
      'turkish': 'tr',
      'hebrew': 'he',
      'thai': 'th',
      'vietnamese': 'vi',
      'indonesian': 'id',
      'malay': 'ms',
      'tamil': 'ta',
      'telugu': 'te',
      'bengali': 'bn',
      'marathi': 'mr',
      'gujarati': 'gu',
      'punjabi': 'pa',
      'malayalam': 'ml',
      'kannada': 'kn',
      'urdu': 'ur'
    };
    
    return languageMapping[language.toLowerCase()] || language.toLowerCase().slice(0, 2);
  }

  /**
   * Intelligently analyze query to determine search strategies
   */
  private async analyzeQuery(query: string): Promise<QueryAnalysis> {
    const analysis: QueryAnalysis = {
      originalQuery: query,
      actors: [],
      directors: [],
      detectedNames: [],
      keywords: [],
      queryType: 'general',
      confidence: 0.5
    };

    // Extract people from query using TMDB
    const peopleData = await this.extractPeopleFromQuery(query);
    analysis.actors = peopleData.actors;
    analysis.directors = peopleData.directors;
    analysis.detectedNames = peopleData.detectedNames;

    // Extract year
    const yearMatch = query.match(/\b(19|20)\d{2}\b/);
    if (yearMatch) {
      analysis.year = yearMatch[0];
    }

    // Extract genre
    const genres = [
      'action', 'adventure', 'animation', 'comedy', 'crime', 'documentary',
      'drama', 'family', 'fantasy', 'horror', 'music', 'mystery', 'romance',
      'science fiction', 'sci-fi', 'thriller', 'war', 'western', 'superhero',
      'biographical', 'biopic', 'historical'
    ];
    
    const lowerQuery = query.toLowerCase();
    for (const genre of genres) {
      if (lowerQuery.includes(genre)) {
        analysis.genre = genre === 'sci-fi' ? 'science fiction' : genre;
        break;
      }
    }

    // Extract production companies
    const productionCompanies = [
      'marvel', 'dc', 'disney', 'pixar', 'warner bros', 'universal', 'paramount',
      'sony', 'fox', '20th century', 'mgm', 'columbia', 'dreamworks', 'netflix',
      'amazon', 'hbo', 'a24', 'blumhouse', 'studio ghibli'
    ];
    
    for (const company of productionCompanies) {
      if (lowerQuery.includes(company)) {
        analysis.productionCompany = company;
        break;
      }
    }

    // Extract language
    const languages = [
      // English variations
      'english', 'english language', 'english movies', 'english films',
      // Popular languages
      'hindi', 'hindi movies', 'hindi films', 'bollywood',
      'spanish', 'spanish movies', 'spanish films', 'spanish language',
      'french', 'french movies', 'french films', 'french language',
      'italian', 'italian movies', 'italian films', 'italian language',
      'german', 'german movies', 'german films', 'german language',
      'japanese', 'japanese movies', 'japanese films', 'japanese language',
      'korean', 'korean movies', 'korean films', 'korean language', 'k-movies',
      'chinese', 'chinese movies', 'chinese films', 'chinese language', 'mandarin',
      'russian', 'russian movies', 'russian films', 'russian language',
      'portuguese', 'portuguese movies', 'portuguese films', 'portuguese language',
      'arabic', 'arabic movies', 'arabic films', 'arabic language',
      'tamil', 'tamil movies', 'tamil films', 'kollywood',
      'telugu', 'telugu movies', 'telugu films', 'tollywood',
      'malayalam', 'malayalam movies', 'malayalam films', 'mollywood',
      'kannada', 'kannada movies', 'kannada films', 'sandalwood',
      'bengali', 'bengali movies', 'bengali films',
      'marathi', 'marathi movies', 'marathi films',
      'punjabi', 'punjabi movies', 'punjabi films',
      'gujarati', 'gujarati movies', 'gujarati films',
      'odia', 'odia movies', 'odia films',
      'assamese', 'assamese movies', 'assamese films',
      'thai', 'thai movies', 'thai films', 'thai language',
      'vietnamese', 'vietnamese movies', 'vietnamese films',
      'indonesian', 'indonesian movies', 'indonesian films',
      'dutch', 'dutch movies', 'dutch films', 'dutch language',
      'swedish', 'swedish movies', 'swedish films', 'swedish language',
      'norwegian', 'norwegian movies', 'norwegian films',
      'danish', 'danish movies', 'danish films', 'danish language',
      'polish', 'polish movies', 'polish films', 'polish language',
      'czech', 'czech movies', 'czech films', 'czech language',
      'hungarian', 'hungarian movies', 'hungarian films',
      'finnish', 'finnish movies', 'finnish films', 'finnish language',
      'greek', 'greek movies', 'greek films', 'greek language',
      'hebrew', 'hebrew movies', 'hebrew films', 'hebrew language',
      'turkish', 'turkish movies', 'turkish films', 'turkish language'
    ];

    // Language mapping to ISO codes for TMDB API
    const languageMapping: { [key: string]: string } = {
      'english': 'en',
      'hindi': 'hi',
      'bollywood': 'hi',
      'spanish': 'es',
      'french': 'fr',
      'italian': 'it',
      'german': 'de',
      'japanese': 'ja',
      'korean': 'ko',
      'k-movies': 'ko',
      'chinese': 'zh',
      'mandarin': 'zh',
      'russian': 'ru',
      'portuguese': 'pt',
      'arabic': 'ar',
      'tamil': 'ta',
      'kollywood': 'ta',
      'telugu': 'te',
      'tollywood': 'te',
      'malayalam': 'ml',
      'mollywood': 'ml',
      'kannada': 'kn',
      'sandalwood': 'kn',
      'bengali': 'bn',
      'marathi': 'mr',
      'punjabi': 'pa',
      'gujarati': 'gu',
      'odia': 'or',
      'assamese': 'as',
      'thai': 'th',
      'vietnamese': 'vi',
      'indonesian': 'id',
      'dutch': 'nl',
      'swedish': 'sv',
      'norwegian': 'no',
      'danish': 'da',
      'polish': 'pl',
      'czech': 'cs',
      'hungarian': 'hu',
      'finnish': 'fi',
      'greek': 'el',
      'hebrew': 'he',
      'turkish': 'tr'
    };
    
    for (const lang of languages) {
      if (lowerQuery.includes(lang)) {
        // Extract base language name (remove 'movies', 'films', 'language')
        const baseLang = lang.replace(/\s+(movies|films|language)$/i, '');
        analysis.language = languageMapping[baseLang] || baseLang;
        break;
      }
    }

    // Extract keywords (remaining meaningful words)
    const stopWords = ['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'from', 'about', 'movie', 'film', 'movies', 'films', 'language'];
    const words = query.toLowerCase().split(/\s+/);
    analysis.keywords = words.filter(word => 
      word.length > 2 && 
      !stopWords.includes(word) &&
      !analysis.actors.some(actor => actor.toLowerCase().includes(word)) &&
      !analysis.directors.some(director => director.toLowerCase().includes(word)) &&
      !(analysis.genre && analysis.genre.toLowerCase().includes(word)) &&
      !(analysis.productionCompany && analysis.productionCompany.toLowerCase().includes(word)) &&
      !(analysis.language && (languages.some(lang => lang.includes(word)) || word === analysis.language))
    );

    // Determine remaining title
    let remainingQuery = query;
    [...(analysis.detectedNames || []), analysis.genre, analysis.productionCompany, analysis.language, analysis.year?.toString()]
      .filter(Boolean)
      .forEach(term => {
        remainingQuery = remainingQuery.replace(new RegExp(term!, 'gi'), '').trim();
      });

    // Also remove language-related phrases
    if (analysis.language) {
      const languageTerms = languages.filter(lang => lowerQuery.includes(lang));
      languageTerms.forEach(term => {
        remainingQuery = remainingQuery.replace(new RegExp(term, 'gi'), '').trim();
      });
    }
    
    // Clean up remaining query
    remainingQuery = remainingQuery.replace(/\s+/g, ' ').trim();
    if (remainingQuery && remainingQuery.length > 1) {
      analysis.title = remainingQuery;
    }

    // Determine query type and confidence
    this.determineQueryTypeAndConfidence(analysis);

    return analysis;
  }

  /**
   * Determine query type and confidence based on analysis
   */
  private determineQueryTypeAndConfidence(analysis: QueryAnalysis): void {
    const hasActors = analysis.actors.length > 0;
    const hasDirectors = analysis.directors.length > 0;
    const hasTitle = !!analysis.title;
    const hasGenre = !!analysis.genre;
    const hasProduction = !!analysis.productionCompany;
    const hasLanguage = !!analysis.language;

    if (hasActors || hasDirectors) {
      if (hasTitle || hasGenre || hasLanguage) {
        analysis.queryType = 'mixed';
        analysis.confidence = 0.8;
      } else {
        analysis.queryType = 'person-focused';
        analysis.confidence = 0.9;
      }
    } else if (hasProduction) {
      analysis.queryType = 'production-focused';
      analysis.confidence = 0.7;
    } else if (hasLanguage && !hasTitle && !hasGenre) {
      analysis.queryType = 'language-focused';
      analysis.confidence = 0.6;
    } else if (hasGenre && !hasTitle) {
      analysis.queryType = 'genre-focused';
      analysis.confidence = 0.6;
    } else if (hasTitle) {
      analysis.queryType = 'title-focused';
      analysis.confidence = 0.7;
    } else {
      analysis.queryType = 'general';
      analysis.confidence = 0.3;
    }
  }

  /**
   * Parse natural language search query into structured components (legacy method)
   */
  private parseSearchQuery(query: string): AdvancedSearchQuery {
    const result: AdvancedSearchQuery = {
      combined: query
    };

    const lowerQuery = query.toLowerCase();

    // Extract year (4-digit number)
    const yearMatch = query.match(/\b(19|20)\d{2}\b/);
    if (yearMatch) {
      result.year = yearMatch[0];
      // Remove year from query for further processing
      query = query.replace(yearMatch[0], '').trim();
    }

    // Check for director indicators
    const directorKeywords = ['director', 'directed by', 'dir by', 'by', 'from'];
    for (const keyword of directorKeywords) {
      if (lowerQuery.includes(keyword)) {
        // Extract director name after the keyword
        const directorMatch = query.match(new RegExp(`${keyword}\\s+([\\w\\s]+?)(?:\\s+(?:movie|film|starring|with|action|drama|comedy|thriller|horror|sci-fi|romance)|$)`, 'i'));
        if (directorMatch) {
          result.director = directorMatch[1].trim();
          query = query.replace(directorMatch[0], '').trim();
        }
        break;
      }
    }

    // Check for actor/cast indicators
    const actorKeywords = ['starring', 'with', 'actor', 'cast', 'featuring'];
    for (const keyword of actorKeywords) {
      if (lowerQuery.includes(keyword)) {
        const actorMatch = query.match(new RegExp(`${keyword}\\s+([\\w\\s]+?)(?:\\s+(?:and|directed|from|action|drama|comedy|thriller|horror|sci-fi|romance)|$)`, 'i'));
        if (actorMatch) {
          result.cast = [actorMatch[1].trim()];
          query = query.replace(actorMatch[0], '').trim();
        }
        break;
      }
    }

    // Check for genre keywords
    const genres = [
      'action', 'adventure', 'animation', 'comedy', 'crime', 'documentary',
      'drama', 'family', 'fantasy', 'history', 'horror', 'music', 'mystery',
      'romance', 'science fiction', 'sci-fi', 'thriller', 'war', 'western'
    ];

    for (const genre of genres) {
      if (lowerQuery.includes(genre)) {
        result.genre = genre === 'sci-fi' ? 'science fiction' : genre;
        query = query.replace(new RegExp(genre, 'gi'), '').trim();
        break;
      }
    }

    // If no specific indicators found, check if query contains known actor/director names
    if (!result.cast && !result.director && !result.genre) {
      // This could be enhanced with a database of known names
      const possibleNames = this.extractPotentialNames(query);
      if (possibleNames.length > 0) {
        // For queries like "the good clint eastwood", treat the name as an actor
        result.cast = possibleNames;
        // Remove the name from the query to leave the movie title
        for (const name of possibleNames) {
          query = query.replace(new RegExp(name, 'gi'), '').trim();
        }
      }
    }

    // Remaining text is likely the movie title
    if (query.trim() && !result.director && !result.cast?.length) {
      result.title = query.trim();
    }

    return result;
  }

  /**
   * Enhanced people extraction with better crew role detection
   */
  private async extractPeopleFromQuery(query: string): Promise<{ actors: string[]; directors: string[]; detectedNames: string[] }> {
    const result: { actors: string[]; directors: string[]; detectedNames: string[] } = { 
      actors: [], 
      directors: [], 
      detectedNames: [] 
    };
    
    try {
      // First, extract potential names using heuristics
      const potentialNames = this.extractPotentialNames(query);
      
      // Search TMDB for each potential name with enhanced filtering
      for (const name of potentialNames) {
        const personResponse = await fetch(
          `${TMDB_BASE_URL}/search/person?api_key=${this.apiKey}&query=${encodeURIComponent(name)}&include_adult=false&page=1`
        );
        
        if (personResponse.ok) {
          const personData = await personResponse.json();
          if (personData.results.length > 0) {
            // Take the most popular match (first result is sorted by popularity)
            const person = personData.results[0];
            result.detectedNames.push(person.name);
            
            // Enhanced categorization using multiple signals
            const knownFor = person.known_for_department;
            const popularity = person.popularity;
            
            // Primary categorization based on known_for_department
            if (knownFor === 'Acting') {
              result.actors.push(person.name);
            } else if (knownFor === 'Directing') {
              result.directors.push(person.name);
            } else if (knownFor === 'Writing') {
              // Writers can also be searched as crew
              result.directors.push(person.name); // Include writers in director search for crew credits
            } else if (knownFor === 'Production') {
              // Producers are also important crew members
              result.directors.push(person.name);
            } else {
              // For ambiguous cases, check their known_for works to determine primary role
              const movieWorks = person.known_for?.filter((work: any) => work.media_type === 'movie') || [];
              
              // If they're known primarily for movies, try to determine their role
              if (movieWorks.length > 0) {
                // Get detailed person info to check credits distribution
                await this.categorizePersonByCredits(person.id, person.name, result);
              } else {
                // Default to actor for high popularity, director for lower popularity
                if (popularity > 5) {
                  result.actors.push(person.name);
                } else {
                  result.directors.push(person.name);
                }
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error extracting people from query:', error);
    }
    
    return result;
  }

  /**
   * Helper method to categorize person based on their actual credits distribution
   */
  private async categorizePersonByCredits(personId: number, personName: string, result: { actors: string[]; directors: string[]; detectedNames: string[] }): Promise<void> {
    try {
      const creditsResponse = await fetch(
        `${TMDB_BASE_URL}/person/${personId}/movie_credits?api_key=${this.apiKey}`
      );
      
      if (creditsResponse.ok) {
        const creditsData = await creditsResponse.json();
        const castCount = creditsData.cast?.length || 0;
        const crewCount = creditsData.crew?.length || 0;
        
        // Count directing credits specifically
        const directingCount = creditsData.crew?.filter((credit: any) => credit.job === 'Director').length || 0;
        const producingCount = creditsData.crew?.filter((credit: any) => 
          credit.job === 'Producer' || credit.job === 'Executive Producer'
        ).length || 0;
        const writingCount = creditsData.crew?.filter((credit: any) => 
          credit.department === 'Writing'
        ).length || 0;
        
        // Categorize based on credit distribution
        if (castCount > crewCount * 2) {
          // Primarily an actor
          result.actors.push(personName);
        } else if (directingCount > 3 || (directingCount > 0 && directingCount >= castCount)) {
          // Significant directing experience
          result.directors.push(personName);
        } else if (producingCount > 5 || writingCount > 3) {
          // Primarily crew roles
          result.directors.push(personName);
        } else {
          // Balanced or unclear - add to both for comprehensive search
          if (castCount > 0) result.actors.push(personName);
          if (crewCount > 0) result.directors.push(personName);
        }
      }
    } catch (error) {
      console.error('Error categorizing person by credits:', error);
      // Fallback: add to actors list
      result.actors.push(personName);
    }
  }

  /**
   * Extract potential person names using heuristics
   */
  private extractPotentialNames(query: string): string[] {
    const words = query.split(' ');
    const potentialNames: string[] = [];
    
    // Look for capitalized word patterns that could be names
    for (let i = 0; i < words.length - 1; i++) {
      const word1 = words[i];
      const word2 = words[i + 1];
      
      // Check if we have two capitalized words (likely a name)
      if (word1.match(/^[A-Z][a-z]+$/) && word2.match(/^[A-Z][a-z]+$/)) {
        potentialNames.push(`${word1} ${word2}`);
        i++; // Skip next word since we used it
      }
    }
    
    // Also check for single capitalized words that might be surnames
    const commonFirstNames = ['the', 'a', 'an', 'in', 'on', 'at', 'by', 'for', 'with', 'from'];
    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      if (word.match(/^[A-Z][a-z]+$/) && !commonFirstNames.includes(word.toLowerCase())) {
        // Check if it's not already part of a two-word name
        const alreadyIncluded = potentialNames.some(name => name.includes(word));
        if (!alreadyIncluded) {
          potentialNames.push(word);
        }
      }
    }
    
    return potentialNames;
  }

  /**
   * Search movies by title with optional year filter
   */
  private async searchByTitle(title: string, year?: string | number, priority: number = 7, language?: string): Promise<EnhancedSearchResult[]> {
    try {
      const searchQuery = year ? `${title} ${year}` : title;
      const response = await fetch(
        `${TMDB_BASE_URL}/search/movie?api_key=${this.apiKey}&query=${encodeURIComponent(searchQuery)}&include_adult=false`
      );

      if (!response.ok) return [];

      const data: TMDbSearchResult = await response.json();
      
      // Convert movies to standard format
      const convertedMovies = await Promise.all(
        data.results.slice(0, 10).map(async movie => {
          const convertedMovie = await this.convertToStandardFormat(movie);
          return {
            movie: convertedMovie,
            relevanceScore: this.calculateTitleRelevance(movie.title, title, year),
            matchedFields: ['title']
          };
        })
      );
      
      return convertedMovies;
    } catch (error) {
      console.error('Error searching by title:', error);
      return [];
    }
  }

  /**
   * Search movies by actor name with optional language filter
   */
  private async searchByActor(actorName: string, genre?: string, priority: number = 8, language?: string): Promise<EnhancedSearchResult[]> {
    try {
      // First, find the actor
      const personResponse = await fetch(
        `${TMDB_BASE_URL}/search/person?api_key=${this.apiKey}&query=${encodeURIComponent(actorName)}`
      );

      if (!personResponse.ok) return [];

      const personData = await personResponse.json();
      if (personData.results.length === 0) return [];

      const actor = personData.results[0];
      
      // Get actor's movie credits
      const creditsResponse = await fetch(
        `${TMDB_BASE_URL}/person/${actor.id}/movie_credits?api_key=${this.apiKey}`
      );

      if (!creditsResponse.ok) return [];

      const creditsData = await creditsResponse.json();
      
      // Filter by language if specified
      let movieCredits = creditsData.cast;
      if (language) {
        movieCredits = movieCredits.filter((movie: any) => movie.original_language === language);
        console.log(`🌍 Filtered ${actor.name}'s movies by language ${language}: ${movieCredits.length} results`);
      }
      
      // Convert movies to standard format
      const convertedMovies = await Promise.all(
        movieCredits.slice(0, 30).map(async (movie: any) => {
          const convertedMovie = await this.convertToStandardFormat(movie);
          return {
            movie: convertedMovie,
            relevanceScore: this.calculateActorRelevance(movie, actor.name, genre, language),
            matchedFields: ['cast']
          };
        })
      );
      
      return convertedMovies;
    } catch (error) {
      console.error('Error searching by actor:', error);
      return [];
    }
  }

  /**
   * Enhanced search by director and key crew members with optional language filter
   */
  private async searchByDirector(directorName: string, genre?: string, priority: number = 8, language?: string): Promise<EnhancedSearchResult[]> {
    try {
      // First, find the director/crew member
      const personResponse = await fetch(
        `${TMDB_BASE_URL}/search/person?api_key=${this.apiKey}&query=${encodeURIComponent(directorName)}&include_adult=false`
      );

      if (!personResponse.ok) return [];

      const personData = await personResponse.json();
      if (personData.results.length === 0) return [];

      const director = personData.results[0];
      
      // Get director's movie credits
      const creditsResponse = await fetch(
        `${TMDB_BASE_URL}/person/${director.id}/movie_credits?api_key=${this.apiKey}`
      );

      if (!creditsResponse.ok) return [];

      const creditsData = await creditsResponse.json();
      
      // Enhanced crew filtering - include multiple important roles
      const relevantCrewMovies = creditsData.crew.filter((credit: any) => {
        const importantJobs = [
          'Director', 
          'Producer', 
          'Executive Producer',
          'Screenplay', 
          'Writer', 
          'Story',
          'Cinematographer',
          'Editor',
          'Music',
          'Original Music Composer'
        ];
        const importantDepartments = ['Directing', 'Writing', 'Production', 'Camera', 'Editing', 'Sound'];
        
        return importantJobs.includes(credit.job) || importantDepartments.includes(credit.department);
      });

      // Filter by language if specified  
      const filteredCrewMovies = language ? 
        relevantCrewMovies.filter((movie: any) => movie.original_language === language) : 
        relevantCrewMovies;

      // Combine crew movies with any cast appearances for actor-directors
      const allRelevantMovies = [...filteredCrewMovies];
      
      // If person is known for acting too, include their top acting credits
      if (director.known_for_department === 'Acting' || creditsData.cast.length > 5) {
        const topActingCredits = creditsData.cast
          .sort((a: any, b: any) => b.popularity - a.popularity)
          .slice(0, 5); // Top 5 acting roles
        allRelevantMovies.push(...topActingCredits);
      }

      // Remove duplicates and sort by popularity/vote_average
      const uniqueMovies = allRelevantMovies.reduce((acc: any[], movie: any) => {
        if (!acc.find(m => m.id === movie.id)) {
          acc.push(movie);
        }
        return acc;
      }, []);

      // Sort by a combination of popularity and rating
      uniqueMovies.sort((a: any, b: any) => {
        const scoreA = (a.popularity || 0) * 0.7 + (a.vote_average || 0) * 0.3;
        const scoreB = (b.popularity || 0) * 0.7 + (b.vote_average || 0) * 0.3;
        return scoreB - scoreA;
      });

      // Convert movies to standard format
      const convertedMovies = await Promise.all(
        uniqueMovies.slice(0, 25).map(async (movie: any) => {
          const convertedMovie = await this.convertToStandardFormat(movie);
          
          // Determine matched fields based on the person's role in this movie
          const crewRole = relevantCrewMovies.find((c: any) => c.id === movie.id);
          const matchedFields = crewRole ? 
            [`crew_${crewRole.job?.toLowerCase().replace(/\s+/g, '_')}`, 'director'] : 
            ['cast', 'actor'];
          
          return {
            movie: convertedMovie,
            relevanceScore: this.calculateDirectorRelevance(movie, director.name, genre, language),
            matchedFields
          };
        })
      );
      
      return convertedMovies;
    } catch (error) {
      console.error('Error searching by director:', error);
      return [];
    }
  }

  /**
   * Search movies by genre
   */
  private async searchByGenre(genreName: string, year?: string | number, priority: number = 6, language?: string): Promise<EnhancedSearchResult[]> {
    try {
      // Get genre list to find genre ID
      const genresResponse = await fetch(
        `${TMDB_BASE_URL}/genre/movie/list?api_key=${this.apiKey}`
      );

      if (!genresResponse.ok) return [];

      const genresData = await genresResponse.json();
      const genre = genresData.genres.find((g: any) => 
        g.name.toLowerCase().includes(genreName.toLowerCase())
      );

      if (!genre) return [];

      // Search movies by genre with language filtering
      const yearParam = year ? `&year=${year}` : '';
      const languageParam = language ? `&with_original_language=${this.getLanguageCode(language)}` : '';
      const discoverResponse = await fetch(
        `${TMDB_BASE_URL}/discover/movie?api_key=${this.apiKey}&with_genres=${genre.id}&sort_by=popularity.desc${yearParam}${languageParam}&include_adult=false`
      );

      if (!discoverResponse.ok) return [];

      const discoverData = await discoverResponse.json();
      
      // Convert movies to standard format
      const convertedMovies = await Promise.all(
        discoverData.results.slice(0, 15).map(async (movie: any) => {
          const convertedMovie = await this.convertToStandardFormat(movie);
          return {
            movie: convertedMovie,
            relevanceScore: this.calculateGenreRelevance(movie, genreName, year),
            matchedFields: ['genre']
          };
        })
      );
      
      return convertedMovies;
    } catch (error) {
      console.error('Error searching by genre:', error);
      return [];
    }
  }

  /**
   * Calculate relevance score for title matches
   */
  private calculateTitleRelevance(movieTitle: string, searchTitle: string, year?: string | number): number {
    let score = 0.5; // Base score for attempting a title match

    const normalizedMovieTitle = this.normalizeTitle(movieTitle);
    const normalizedSearchTitle = this.normalizeTitle(searchTitle);

    if (!normalizedMovieTitle || !normalizedSearchTitle) {
      return score;
    }

    if (normalizedMovieTitle === normalizedSearchTitle) {
      score += 0.45;
    } else if (normalizedMovieTitle.includes(normalizedSearchTitle)) {
      score += 0.35;
    } else {
      const wordOverlap = this.calculateTitleWordOverlap(movieTitle, searchTitle);
      if (wordOverlap > 0) {
        score += Math.min(0.35, wordOverlap * 0.4);
      }

      const similarity = this.titleSimilarity(normalizedMovieTitle, normalizedSearchTitle);
      if (similarity > 0) {
        score += Math.min(0.3, similarity * 0.25);
      }
    }

    if (year && movieTitle.includes(year.toString())) {
      score += 0.2;
    }

    return Math.min(score, 1.0);
  }

  /**
   * Calculate title similarity using Levenshtein distance
   */
  private titleSimilarity(title1: string, title2: string): number {
    if (!title1 || !title2) return 0;
    
    const str1 = title1.toLowerCase().trim();
    const str2 = title2.toLowerCase().trim();
    
    if (str1 === str2) return 1.0;
    
    const len1 = str1.length;
    const len2 = str2.length;
    
    // Simple similarity based on common words and character overlap
    const words1 = str1.split(/\s+/);
    const words2 = str2.split(/\s+/);
    
    const commonWords = words1.filter(word => 
      words2.some(w2 => w2.includes(word) || word.includes(w2))
    ).length;
    
    const maxWords = Math.max(words1.length, words2.length);
    const wordSimilarity = commonWords / maxWords;
    
    // Character similarity (simplified)
    const maxLen = Math.max(len1, len2);
    const minLen = Math.min(len1, len2);
    const lengthSimilarity = minLen / maxLen;
    
    // Combined similarity
    return (wordSimilarity * 0.7 + lengthSimilarity * 0.3);
  }

  /**
   * Normalize titles by removing punctuation and collapsing whitespace
   */
  private normalizeTitle(title: string): string {
    if (!title) {
      return '';
    }

    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Calculate overlap between meaningful words in the movie and search titles
   */
  private calculateTitleWordOverlap(movieTitle: string, searchTitle: string): number {
    const movieWords = new Set(this.getMeaningfulTitleWords(movieTitle));
    const searchWords = this.getMeaningfulTitleWords(searchTitle);

    if (searchWords.length === 0) {
      return 0;
    }

    let matchCount = 0;
    searchWords.forEach(word => {
      if (movieWords.has(word)) {
        matchCount++;
      }
    });

    return matchCount / searchWords.length;
  }

  /**
   * Extract meaningful (non-stop) words from a title
   */
  private getMeaningfulTitleWords(title: string): string[] {
    const normalizedTitle = this.normalizeTitle(title);
    if (!normalizedTitle) {
      return [];
    }

    return normalizedTitle
      .split(' ')
      .filter(word => word.length > 2 && !TMDbService.TITLE_STOP_WORDS.has(word));
  }

  /**
   * Calculate relevance score for title + language matches
   */
  private calculateTitleLanguageRelevance(movie: any, title: string, languageCode: string, priority: number): number {
    let score = priority / 10; // Base score from priority
    
    // Title match bonus
    const titleSim = this.titleSimilarity(movie.title, title);
    const originalTitleSim = this.titleSimilarity(movie.original_title, title);
    const bestTitleMatch = Math.max(titleSim, originalTitleSim);
    
    score += bestTitleMatch * 0.4; // Strong title match bonus
    
    // Language match bonus
    if (movie.original_language === languageCode) {
      score += 0.3;
    }
    
    // Popularity bonus
    if (movie.popularity > 10) score += 0.1;
    if (movie.vote_average > 7) score += 0.1;
    
    return Math.min(1.0, score);
  }

  /**
   * Calculate relevance for combined terms search
   */
  private calculateCombinedTermsRelevance(movie: any, combinedQuery: string, priority: number): number {
    let score = priority / 10;
    
    const lowerQuery = combinedQuery.toLowerCase();
    const lowerTitle = movie.title?.toLowerCase() || '';
    const lowerOriginalTitle = movie.original_title?.toLowerCase() || '';
    const lowerOverview = movie.overview?.toLowerCase() || '';
    
    // Direct match bonuses
    if (lowerTitle.includes(lowerQuery)) score += 0.4;
    if (lowerOriginalTitle.includes(lowerQuery)) score += 0.3;
    if (lowerOverview.includes(lowerQuery)) score += 0.2;
    
    // Partial word matches
    const queryWords = lowerQuery.split(/\s+/);
    const titleWords = lowerTitle.split(/\s+/);
    
    const matchingWords = queryWords.filter(qWord => 
      titleWords.some((tWord: string) => tWord.includes(qWord) || qWord.includes(tWord))
    ).length;
    
    score += (matchingWords / queryWords.length) * 0.3;
    
    // Popularity bonus
    if (movie.popularity > 20) score += 0.1;
    
    return Math.min(1.0, score);
  }

  /**
   * Calculate relevance for multi-keyword search
   */
  private calculateMultiKeywordRelevance(movie: any, searchTerms: string[], priority: number): number {
    const totalTerms = searchTerms.length || 1;
    let score = 0.2 + Math.min(0.3, (priority / 10) * 0.3);
    
    const lowerTitle = movie.title?.toLowerCase() || '';
    const lowerOriginalTitle = movie.original_title?.toLowerCase() || '';
    const lowerOverview = movie.overview?.toLowerCase() || '';
    
    // Check how many terms match
    let matchCount = 0;
    searchTerms.forEach(term => {
      const lowerTerm = term.toLowerCase();
      if (lowerTitle.includes(lowerTerm) || 
          lowerOriginalTitle.includes(lowerTerm) || 
          lowerOverview.includes(lowerTerm)) {
        matchCount++;
      }
    });
    
    const coverage = matchCount / totalTerms;
    
    // Boost score based on match count
    score += coverage * 0.5;
    
    // Full phrase bonus
    const fullPhrase = searchTerms.join(' ').toLowerCase();
    if (fullPhrase && lowerTitle.includes(fullPhrase)) {
      score += 0.25;
    } else if (coverage >= 0.75) {
      score += 0.1;
    }
    
    // Popularity and rating bonuses (light weight)
    if (movie.popularity) {
      score += Math.min(0.1, movie.popularity / 200);
    }
    if (movie.vote_average) {
      score += Math.min(0.1, movie.vote_average / 100);
    }
    
    return Math.min(0.99, score);
  }

  /**
   * Calculate relevance score for actor matches
   */
  private calculateActorRelevance(movie: any, actorName: string, genre?: string, language?: string): number {
    let score = 0.6; // Base score for actor match

    // Higher score for more popular movies
    if (movie.popularity > 10) score += 0.1;
    if (movie.popularity > 50) score += 0.1;

    // Genre match bonus
    if (genre && movie.genre_ids) {
      // This would need genre ID mapping, simplified for now
      score += 0.1;
    }

    // Language match bonus
    if (language && movie.original_language === language) {
      score += 0.15;
    }

    // Recent movies get slight boost
    if (movie.release_date) {
      const releaseYear = new Date(movie.release_date).getFullYear();
      const currentYear = new Date().getFullYear();
      if (currentYear - releaseYear < 5) {
        score += 0.1;
      }
    }

    return Math.min(score, 1.0);
  }

  /**
   * Calculate relevance score for director matches
   */
  private calculateDirectorRelevance(movie: any, directorName: string, genre?: string, language?: string): number {
    let score = 0.7; // Base score for director match (higher than actor)

    // Higher score for more popular movies
    if (movie.popularity > 10) score += 0.1;
    if (movie.popularity > 50) score += 0.1;

    // Genre match bonus
    if (genre && movie.genre_ids) {
      score += 0.1;
    }

    // Language match bonus
    if (language && movie.original_language === language) {
      score += 0.15;
    }

    return Math.min(score, 1.0);
  }

  /**
   * Calculate relevance score for genre matches
   */
  private calculateGenreRelevance(movie: any, genreName: string, year?: string | number): number {
    let score = 0.4; // Base score for genre match

    // Popularity boost
    if (movie.vote_average > 7) score += 0.2;
    if (movie.vote_count > 1000) score += 0.1;

    // Year match bonus
    if (year && movie.release_date) {
      const movieYear = new Date(movie.release_date).getFullYear();
      if (movieYear.toString() === year.toString()) {
        score += 0.3;
      }
    }

    return Math.min(score, 1.0);
  }

  /**
   * Remove duplicates and combine scores for same movies
   */
  private deduplicateAndScore(results: EnhancedSearchResult[]): EnhancedSearchResult[] {
    const movieMap = new Map<number, EnhancedSearchResult>();

    for (const result of results) {
      const movieId = result.movie.id;
      
      if (movieMap.has(movieId)) {
        const existing = movieMap.get(movieId)!;
        // Combine scores and matched fields
        existing.relevanceScore = Math.max(existing.relevanceScore, result.relevanceScore);
        const combinedFields = new Set([...existing.matchedFields, ...result.matchedFields]);
        existing.matchedFields = Array.from(combinedFields);
      } else {
        movieMap.set(movieId, result);
      }
    }

    return Array.from(movieMap.values());
  }

  private async getDirectTitleMatches(query: string, page: number = 1): Promise<EnhancedSearchResult[]> {
    const trimmedQuery = query.trim();
    if (trimmedQuery.length < 2) {
      return [];
    }

    try {
      const directSearch = await this.searchMovies(trimmedQuery, page, false);
      if (directSearch.Response !== 'True' || !Array.isArray(directSearch.Search)) {
        return [];
      }

      return directSearch.Search.slice(0, 10).map(movie => ({
        movie,
        relevanceScore: 0.97,
        matchedFields: ['direct_title_match']
      }));
    } catch (error) {
      console.warn('⚠️ Direct TMDB title match lookup failed:', error);
      return [];
    }
  }

  private buildEnhancedSearchResponse(allResults: EnhancedSearchResult[], page: number, originalQuery: string): EnhancedSearchResponse {
    if (!allResults || allResults.length === 0) {
      return {
        Search: [],
        totalResults: '0',
        Response: 'False',
        actualTotal: 0,
        hasMore: false,
        currentPage: page,
        totalPages: 0
      };
    }

    const uniqueResults = this.deduplicateAndScore(allResults);
    const boostedResults = uniqueResults.map(result => this.applyTitleSimilarityBoost(result, originalQuery));

    const getPriority = (result: EnhancedSearchResult): number => {
      const fields = result.matchedFields || [];
      if (fields.includes('direct_title_match') || fields.includes('title_exact_match')) {
        return 3;
      }
      if (fields.includes('title_phrase_match') || fields.includes('title_similarity')) {
        return 2;
      }
      return 1;
    };

    const sortedResults = boostedResults.sort((a, b) => {
      const priorityDiff = getPriority(b) - getPriority(a);
      if (priorityDiff !== 0) return priorityDiff;
      return (b.relevanceScore || 0) - (a.relevanceScore || 0);
    });

    const resultsPerPage = 20;
    const startIndex = (page - 1) * resultsPerPage;
    const endIndex = startIndex + resultsPerPage;
    const paginatedResults = sortedResults.slice(startIndex, endIndex);
    const hasMore = endIndex < sortedResults.length;

    return {
      Search: paginatedResults,
      totalResults: sortedResults.length.toString(),
      Response: 'True',
      actualTotal: sortedResults.length,
      hasMore,
      currentPage: page,
      totalPages: Math.ceil(sortedResults.length / resultsPerPage)
    };
  }

  private applyTitleSimilarityBoost(result: EnhancedSearchResult, query: string): EnhancedSearchResult {
    const movieTitle = result.movie?.Title || result.movie?.title || '';
    if (!movieTitle || !query) {
      return result;
    }

    const normalizedQuery = this.normalizeTitle(query);
    const normalizedTitle = this.normalizeTitle(movieTitle);
    if (!normalizedTitle) {
      return result;
    }

    let boostedScore = result.relevanceScore || 0;
    const matchedFields = new Set(result.matchedFields || []);

    if (normalizedTitle === normalizedQuery) {
      boostedScore = Math.max(boostedScore, 0.995);
      matchedFields.add('title_exact_match');
    } else {
      const similarity = this.titleSimilarity(movieTitle, query);
      if (similarity > 0.4) {
        boostedScore = Math.max(boostedScore, boostedScore + similarity * 0.2);
        matchedFields.add('title_similarity');
      }

      const lowerTitle = movieTitle.toLowerCase();
      const lowerQuery = query.toLowerCase().trim();
      if (lowerTitle.includes(lowerQuery)) {
        boostedScore = Math.max(boostedScore, boostedScore + 0.15);
        matchedFields.add('title_phrase_match');
      }
    }

    return {
      ...result,
      relevanceScore: Math.min(0.999, boostedScore),
      matchedFields: Array.from(matchedFields)
    };
  }

  async getMovieById(id: string): Promise<TMDbMovie> {
    try {
      // Use Firebase Functions if available
      if (shouldUseFunctionsAPI()) {
        return await tmdbFunctionsService.getMovieById(id);
      }

      // Fallback to direct API calls
      const isValid = await this.validateApiKey();
      if (!isValid) {
        console.warn(`TMDb API key invalid, returning fallback data for ${id}`);
        return this.createFallbackMovie(id);
      }

      // Handle both TMDb IDs and IMDB IDs
      let movieData: TMDbMovie;
      
      if (id.startsWith('tt')) {
        // IMDB ID - use find endpoint to get TMDb ID, then fetch full details
        const findResponse = await fetch(
          `${TMDB_BASE_URL}/find/${id}?api_key=${this.apiKey}&external_source=imdb_id`
        );
        
        if (findResponse.status === 401) {
          this.apiKeyValid = false;
          console.warn(`TMDb API key invalid for movie ${id}, returning fallback data`);
          return this.createFallbackMovie(id);
        }
        
        if (!findResponse.ok) {
          throw new Error('Failed to fetch movie details');
        }
        
        const findData = await findResponse.json();
        if (findData.movie_results.length === 0) {
          console.warn(`Movie not found: ${id}`);
          return this.createFallbackMovie(id);
        }
        
        // Now fetch full details with credits using the TMDb ID
        const tmdbId = findData.movie_results[0].id;
        const response = await fetch(
          `${TMDB_BASE_URL}/movie/${tmdbId}?api_key=${this.apiKey}&append_to_response=credits,images,external_ids`
        );
        
        if (!response.ok) {
          throw new Error('Failed to fetch full movie details');
        }
        
        movieData = await response.json();
      } else {
        // TMDb ID, numeric, tmdb_, or legacy movie-<n>
        const tmdbId =
          getTmdbNumericIdForCache(id) ??
          (id.startsWith('tmdb_') ? id.substring(5) : id.startsWith('tmdb') ? id.substring(4) : id);
        const response = await fetch(
          `${TMDB_BASE_URL}/movie/${tmdbId}?api_key=${this.apiKey}&append_to_response=credits,images,external_ids`
        );
        
        if (response.status === 401) {
          this.apiKeyValid = false;
          console.warn(`TMDb API key invalid for movie ${id}, returning fallback data`);
          return this.createFallbackMovie(id);
        }
        
        if (response.status === 404) {
          console.warn(`Movie not found: ${id}`);
          return this.createFallbackMovie(id);
        }
        
        if (!response.ok) {
          throw new Error('Failed to fetch movie details');
        }
        
        movieData = await response.json();
      }
      
      return await this.convertToStandardFormat(movieData);
    } catch (error) {
      console.error('Error fetching movie details:', error);
      return this.createFallbackMovie(id);
    }
  }

  async getMovieByTitle(title: string, year?: string): Promise<TMDbMovie> {
    try {
      // Use Firebase Functions if available
      if (shouldUseFunctionsAPI()) {
        return await tmdbFunctionsService.getMovieByTitle(title, year);
      }

      // Fallback to direct API calls
      const isValid = await this.validateApiKey();
      if (!isValid) {
        console.warn(`TMDb API key invalid, returning fallback data for ${title}`);
        return this.createFallbackMovie(`unknown-${Date.now()}`, title);
      }

      const searchQuery = year ? `${title} ${year}` : title;
      const response = await fetch(
        `${TMDB_BASE_URL}/search/movie?api_key=${this.apiKey}&query=${encodeURIComponent(searchQuery)}&include_adult=false`
      );
      
      if (response.status === 401) {
        this.apiKeyValid = false;
        console.warn(`TMDb API key invalid for movie ${title}, returning fallback data`);
        return this.createFallbackMovie(`unknown-${Date.now()}`, title);
      }
      
      if (!response.ok) {
        throw new Error('Failed to fetch movie details');
      }
      
      const data: TMDbSearchResult = await response.json();
      
      if (data.results.length === 0) {
        console.warn(`Movie not found: ${title}`);
        return this.createFallbackMovie(`unknown-${Date.now()}`, title);
      }
      
      // Find best match by year if provided
      let bestMatch = data.results[0];
      if (year) {
        const yearMatch = data.results.find(movie => 
          movie.release_date && movie.release_date.startsWith(year)
        );
        if (yearMatch) {
          bestMatch = yearMatch;
        }
      }
      
      // Get full details with credits
      return await this.getMovieById(bestMatch.id.toString());
    } catch (error) {
      console.error('Error fetching movie details:', error);
      return this.createFallbackMovie(`unknown-${Date.now()}`, title);
    }
  }

  // Convert TMDb rating to numeric value (for compatibility)
  getNumericRating(ratings: TMDbMovie['Ratings'], tmdbRating: string): number {
    // Try to get TMDb rating first
    if (tmdbRating && tmdbRating !== 'N/A') {
      return parseFloat(tmdbRating);
    }
    
    // Fallback to ratings array
    if (ratings && ratings.length > 0) {
      const rating = ratings[0];
      if (rating.Source === 'The Movie Database') {
        const match = rating.Value.match(/(\d+\.?\d*)/);
        if (match) {
          return parseFloat(match[1]);
        }
      }
    }
    
    return 0;
  }

  // Get high-quality poster URL
  getHighQualityPoster(posterPath: string): string {
    if (!posterPath || posterPath === 'N/A') {
      return 'https://via.placeholder.com/300x450/333/fff?text=No+Image';
    }

    // If it's already a full URL, return as is
    if (posterPath.startsWith('http')) {
      return posterPath;
    }

    // Return high-resolution TMDb image
    return `https://image.tmdb.org/t/p/w780${posterPath}`;
  }

  // Grid-sized poster for search results and small card layouts.
  // w342 is ~5× lighter than w780 and renders identically at typical card widths (~160–200 px).
  getGridPoster(posterPath: string): string {
    if (!posterPath || posterPath === 'N/A') {
      return 'https://via.placeholder.com/300x450/333/fff?text=No+Image';
    }
    if (posterPath.startsWith('http')) {
      return posterPath;
    }
    return `https://image.tmdb.org/t/p/w342${posterPath}`;
  }

  // Get backdrop image
  getBackdropImage(backdropPath: string): string {
    if (!backdropPath) {
      return 'https://via.placeholder.com/1280x720/333/fff?text=No+Image';
    }
    
    return `https://image.tmdb.org/t/p/w1280${backdropPath}`;
  }

  // Check if API key is working
  isApiKeyValid(): boolean {
    return this.apiKeyValid;
  }

  // Get movie genres
  async getGenres(): Promise<Array<{ id: number; name: string }>> {
    try {
      // Use Firebase Functions if available
      if (shouldUseFunctionsAPI()) {
        return await tmdbFunctionsService.getGenres();
      }

      // Fallback to direct API calls
      const response = await fetch(
        `${TMDB_BASE_URL}/genre/movie/list?api_key=${this.apiKey}`
      );
      
      if (!response.ok) {
        return [];
      }
      
      const data = await response.json();
      return data.genres || [];
    } catch (error) {
      console.error('Error fetching genres:', error);
      return [];
    }
  }

  // Get top movies for a specific genre
  async getTopMoviesForGenre(genreId: number, limit: number = 20): Promise<TMDbMovie[]> {
    try {
      console.log(`🎭 TMDbService: Fetching movies for genre ${genreId}, limit ${limit}`);
      
      // Use Firebase Functions if available
      if (shouldUseFunctionsAPI()) {
        console.log('🚀 Using Firebase Functions API for genre movies');
        try {
          const result = await discoverMovies({
            with_genres: genreId.toString(),
            sort_by: 'popularity.desc',
            'vote_count.gte': 100,
            page: 1,
          });
          const movies = (result.results || []).slice(0, limit);
          console.log(`✅ Functions API genre result: ${movies.length} movies for genre ${genreId}`);
          return movies;
        } catch (error) {
          console.error('❌ Functions API failed for genre movies, falling back to direct API:', error);
        }
      }
      
      // Fallback to direct API calls
      const response = await fetch(
        `${TMDB_BASE_URL}/discover/movie?api_key=${this.apiKey}&with_genres=${genreId}&sort_by=popularity.desc&vote_count.gte=100&page=1&append_to_response=genres`
      );
      
      if (!response.ok) {
        console.error(`❌ TMDbService: Failed to fetch movies for genre ${genreId}, status: ${response.status}`);
        return [];
      }
      
      const data = await response.json();
      const movies = data.results || [];
      console.log(`🎭 TMDbService: Found ${movies.length} movies for genre ${genreId} via direct API`);
      
      // Fetch detailed data for top movies to get complete information
      const detailedMovies = await Promise.all(
        movies.slice(0, limit).map(async (movie: any) => {
          try {
            const detailResponse = await fetch(
              `${TMDB_BASE_URL}/movie/${movie.id}?api_key=${this.apiKey}&append_to_response=credits,external_ids`
            );
            
            if (!detailResponse.ok) {
              // Return basic movie data if detail fetch fails
              return {
                ...movie,
                year: movie.release_date ? new Date(movie.release_date).getFullYear().toString() : '',
                runtime: null,
                imdb_id: null,
                genres: []
              };
            }
            
            const detailData = await detailResponse.json();
            return {
              ...detailData,
              year: detailData.release_date ? new Date(detailData.release_date).getFullYear().toString() : ''
            };
          } catch (error) {
            console.error(`Error fetching details for movie ${movie.id}:`, error);
            // Return basic movie data as fallback
            return {
              ...movie,
              year: movie.release_date ? new Date(movie.release_date).getFullYear().toString() : '',
              runtime: null,
              imdb_id: null,
              genres: []
            };
          }
        })
      );
      
      return detailedMovies;
    } catch (error) {
      console.error('Error fetching top movies for genre:', error);
      return [];
    }
  }

  // Get genres with their top movies and poster URLs
  async getGenresWithMovies(): Promise<GenreWithMovies[]> {
    try {
      console.log('🎭 TMDbService: Starting getGenresWithMovies');
      const genres = await this.getGenres();
      console.log(`🎭 TMDbService: Loaded ${genres.length} genres`);
      
      // Get top movies for each genre (limit to first 10 genres for performance)
      const genresWithMovies = await Promise.all(
        genres.slice(0, 10).map(async (genre) => {
          console.log(`🎭 TMDbService: Processing genre: ${genre.name} (${genre.id})`);
          const topMovies = await this.getTopMoviesForGenre(genre.id, 3);
          const posterUrls = topMovies
            .filter(movie => movie.poster_path)
            .map(movie => `${TMDB_IMAGE_BASE_URL}${movie.poster_path}`)
            .slice(0, 3);
          
          console.log(`🎭 TMDbService: Genre ${genre.name} has ${topMovies.length} movies, ${posterUrls.length} posters`);
          
          return {
            ...genre,
            topMovies,
            posterUrls,
            movieCount: topMovies.length
          } as GenreWithMovies;
        })
      );
      
      console.log(`🎭 TMDbService: Completed processing ${genresWithMovies.length} genres with movies`);
      return genresWithMovies;
    } catch (error) {
      console.error('❌ TMDbService: Error fetching genres with movies:', error);
      return [];
    }
  }

  // Get trending movies
  async getTrendingMovies(timeWindow: 'day' | 'week' = 'week'): Promise<TMDbMovie[]> {
    try {
      console.log(`🔥 TMDbService.getTrendingMovies called (${timeWindow})`);
      
      // Use Firebase Functions if available
      if (shouldUseFunctionsAPI()) {
        console.log('🚀 Using Firebase Functions API for trending movies');
        try {
          const result = await tmdbFunctionsService.getTrendingMovies(timeWindow);
          console.log('✅ Functions API trending result:', result.length, 'movies');
          return result;
        } catch (functionsError) {
          console.warn('⚠️ Functions API failed for trending movies, falling back to direct TMDB:', functionsError);
          // Fall through to direct API as fallback
        }
      }

      // Fallback to direct API calls
      console.log('🔄 Using direct TMDB API for trending movies');
      const response = await fetch(
        `${TMDB_BASE_URL}/trending/movie/${timeWindow}?api_key=${this.apiKey}`
      );
      
      if (!response.ok) {
        console.error('❌ Direct TMDB API failed:', response.status, response.statusText);
        return [];
      }
      
      const data = await response.json();
      const convertedMovies = await Promise.all(data.results.map(async (movie: TMDbMovie) => await this.convertToStandardFormat(movie)));
      console.log('✅ Direct TMDB API trending result:', convertedMovies.length, 'movies');
      return convertedMovies;
    } catch (error) {
      console.error('💥 Error fetching trending movies:', error);
      return [];
    }
  }

  // Get recently added movies (latest releases)
  async getRecentlyAddedMovies(): Promise<TMDbMovie[]> {
    try {
      console.log('🎬 TMDbService.getRecentlyAddedMovies called');
      
      // Use Firebase Functions if available
      if (shouldUseFunctionsAPI()) {
        console.log('🚀 Using Firebase Functions API for recently added movies');
        try {
          const result = await tmdbFunctionsService.getRecentlyAddedMovies();
          console.log('✅ Functions API recently added result:', result.length, 'movies');
          return result;
        } catch (functionsError) {
          console.warn('⚠️ Functions API failed for recently added movies, falling back to direct TMDB:', functionsError);
          // Fall through to direct API as fallback
        }
      }

      // Fallback to direct API calls
      console.log('🔄 Using direct TMDB API for recently added movies');
      const response = await fetch(
        `${TMDB_BASE_URL}/movie/now_playing?api_key=${this.apiKey}&language=en-US&page=1`
      );
      
      if (!response.ok) {
        console.error('❌ Direct TMDB API failed:', response.status, response.statusText);
        return [];
      }
      
      const data = await response.json();
      // Get the first 8 recently added movies
      const recentMovies = data.results.slice(0, 8);
      const convertedMovies = await Promise.all(recentMovies.map(async (movie: TMDbMovie) => await this.convertToStandardFormat(movie)));
      console.log('✅ Direct TMDB API recently added result:', convertedMovies.length, 'movies');
      return convertedMovies;
    } catch (error) {
      console.error('💥 Error fetching recently added movies:', error);
      return [];
    }
  }

  // Get popular movies by languages with English priority
  async getPopularMoviesByLanguages(languages: string[] = ['en'], totalCount: number = 40): Promise<TMDbMovie[]> {
    try {
      const isValid = await this.validateApiKey();
      if (!isValid) {
        console.warn('TMDb API key invalid, returning empty array for popular movies by languages');
        return [];
      }

      const allMovies: TMDbMovie[] = [];
      
      // Prioritize English - get more English movies
      const englishCount = Math.ceil(totalCount * 0.6); // 60% English
      const otherLanguagesCount = totalCount - englishCount;
      
      // Get English movies first
      if (languages.includes('en')) {
        const englishMovies = await this.fetchPopularMoviesByLanguage('en', Math.ceil(englishCount / 2)); // Get from 2 pages
        allMovies.push(...englishMovies.slice(0, englishCount));
      }
      
      // Get movies from other selected languages
      const otherLanguages = languages.filter(lang => lang !== 'en');
      if (otherLanguages.length > 0 && otherLanguagesCount > 0) {
        const moviesPerLanguage = Math.ceil(otherLanguagesCount / otherLanguages.length);
        
        for (const language of otherLanguages) {
          const languageMovies = await this.fetchPopularMoviesByLanguage(language, moviesPerLanguage);
          allMovies.push(...languageMovies.slice(0, moviesPerLanguage));
        }
      }
      
      // If we don't have enough movies, fill with general popular movies
      if (allMovies.length < totalCount) {
        const remainingCount = totalCount - allMovies.length;
        const popularMovies = await this.fetchPopularMovies(remainingCount);
        
        // Filter out duplicates
        const existingIds = new Set(allMovies.map(m => m.id));
        const newMovies = popularMovies.filter(m => !existingIds.has(m.id));
        allMovies.push(...newMovies.slice(0, remainingCount));
      }
      
      // Shuffle the array to mix languages while maintaining English priority at the beginning
      const englishMovies = allMovies.filter(m => m.original_language === 'en').slice(0, Math.ceil(totalCount * 0.4));
      const otherMovies = allMovies.filter(m => m.original_language !== 'en');
      const shuffledOthers = this.shuffleArray([...otherMovies]);
      
      // Combine: some English first, then mixed, then remaining English
      const result = [
        ...englishMovies.slice(0, 8), // First 8 English
        ...this.shuffleArray([...englishMovies.slice(8), ...shuffledOthers]) // Mixed remainder
      ].slice(0, totalCount);
      
      console.log(`🎬 TMDbService: Fetched ${result.length} popular movies for languages: ${languages.join(', ')}`);
      return result;
    } catch (error) {
      console.error('Error fetching popular movies by languages:', error);
      return [];
    }
  }

  // Get a mix of popular and top-rated movies by languages with proper language interleaving
  async getPopularAndTopRatedMoviesByLanguages(languages: string[] = ['en'], totalCount: number = 50, page: number = 1): Promise<TMDbMovie[]> {
    try {
      console.log(`🎬 TMDbService.getPopularAndTopRatedMoviesByLanguages called for languages: ${languages.join(', ')}, count: ${totalCount}, page: ${page}`);

      // Use Firebase Functions if available
      if (shouldUseFunctionsAPI()) {
        console.log('🚀 Using Firebase Functions API for popular and top-rated movies by languages');
        try {
          const moviesRaw = await tmdbFunctionsService.getPopularAndTopRatedMoviesByLanguages(languages, totalCount, page);
          const movies = await Promise.all(
            moviesRaw.map(async (movie: any) => await this.convertToStandardFormat(movie))
          );

          console.log('✅ Functions API result:', movies.length, 'movies for onboarding');
          return movies;
        } catch (functionsError) {
          console.error('❌ Functions API failed for popular and top-rated movies by languages. CSP restrictions prevent direct TMDB API fallback:', functionsError);
          // Don't fall back to direct API when CSP is enforced - return empty array instead
          return [];
        }
      }

      const isValid = await this.validateApiKey();
      if (!isValid) {
        console.warn('TMDb API key invalid, returning empty array for popular and top-rated movies by languages');
        return [];
      }

      // Fetch movies for each language separately to enable proper interleaving
      const moviesByLanguage: { [key: string]: { popular: TMDbMovie[]; topRated: TMDbMovie[] } } = {};
      
      // Calculate how many movies to fetch per language
      const moviesPerLanguage = Math.ceil(totalCount * 1.5 / languages.length); // Fetch extra to have selection
      const popularPerLanguage = Math.ceil(moviesPerLanguage * 0.6);
      const topRatedPerLanguage = moviesPerLanguage - popularPerLanguage;
      
      // Fetch movies for each language in parallel
      const fetchPromises = languages.map(async (language) => {
        const [popular, topRated] = await Promise.all([
          this.fetchPopularMoviesByLanguage(language, popularPerLanguage),
          this.fetchTopRatedMoviesByLanguage(language, topRatedPerLanguage)
        ]);
        
        moviesByLanguage[language] = {
          popular: popular,
          topRated: topRated
        };
      });
      
      await Promise.all(fetchPromises);
      
      // Create interleaved array with proper language mixing
      const interleavedMovies: TMDbMovie[] = [];
      const languageQueues = languages.map(lang => ({
        language: lang,
        popular: [...moviesByLanguage[lang]?.popular || []],
        topRated: [...moviesByLanguage[lang]?.topRated || []],
        isEnglish: lang === 'en'
      }));
      
      // Interleave movies from different languages
      let currentIndex = 0;
      const addedMovieIds = new Set<number>();
      
      // Prioritize English but still mix with other languages
      const englishQueue = languageQueues.find(q => q.isEnglish);
      const otherQueues = languageQueues.filter(q => !q.isEnglish);
      
      while (interleavedMovies.length < totalCount) {
        let addedInThisRound = false;
        
        // Add English movie (60% of the time)
        if (englishQueue && (Math.random() < 0.6 || otherQueues.length === 0)) {
          const movie = this.getNextMovieFromQueue(englishQueue, currentIndex % 2 === 0 ? 'popular' : 'topRated');
          if (movie && !addedMovieIds.has(movie.id)) {
            interleavedMovies.push(movie);
            addedMovieIds.add(movie.id);
            addedInThisRound = true;
          }
        }
        
        // Add movie from other languages (40% of the time or when English is exhausted)
        if (otherQueues.length > 0 && (!addedInThisRound || Math.random() < 0.4)) {
          const randomQueue = otherQueues[Math.floor(Math.random() * otherQueues.length)];
          const movie = this.getNextMovieFromQueue(randomQueue, currentIndex % 2 === 0 ? 'popular' : 'topRated');
          if (movie && !addedMovieIds.has(movie.id)) {
            interleavedMovies.push(movie);
            addedMovieIds.add(movie.id);
            addedInThisRound = true;
          }
        }
        
        // If no movies were added in this round, break to avoid infinite loop
        if (!addedInThisRound) {
          // Try to fill remaining slots with any available movies
          const allAvailableMovies = languageQueues.flatMap(q => [...q.popular, ...q.topRated])
            .filter(movie => !addedMovieIds.has(movie.id));
          
          if (allAvailableMovies.length === 0) break;
          
          // Add remaining movies randomly
          const shuffledRemaining = this.shuffleArray(allAvailableMovies);
          for (const movie of shuffledRemaining) {
            if (interleavedMovies.length >= totalCount) break;
            if (!addedMovieIds.has(movie.id)) {
              interleavedMovies.push(movie);
              addedMovieIds.add(movie.id);
            }
          }
          break;
        }
        
        currentIndex++;
      }
      
      // Final shuffle to mix popular and top-rated while maintaining language diversity
      const result = this.shuffleArray(interleavedMovies).slice(0, totalCount);
      
      // Log language distribution for debugging
      const languageDistribution = result.reduce((acc, movie) => {
        acc[movie.original_language] = (acc[movie.original_language] || 0) + 1;
        return acc;
      }, {} as { [key: string]: number });
      
      console.log(`🎬 TMDbService: Fetched ${result.length} interleaved popular and top-rated movies`);
      console.log(`🌍 Language distribution:`, languageDistribution);
      
      return result;
    } catch (error) {
      console.error('Error fetching popular and top-rated movies by languages:', error);
      return [];
    }
  }

  // Helper method to get next movie from a language queue
  private getNextMovieFromQueue(
    queue: { popular: TMDbMovie[]; topRated: TMDbMovie[]; language: string }, 
    type: 'popular' | 'topRated'
  ): TMDbMovie | null {
    const targetQueue = queue[type];
    return targetQueue.length > 0 ? targetQueue.shift() || null : null;
  }

  // Helper function to fetch popular movies for a specific language
  private async fetchPopularMoviesByLanguage(language: string, count: number): Promise<TMDbMovie[]> {
    try {
      const pages = Math.ceil(count / 20); // TMDB returns ~20 movies per page
      const allMovies: TMDbMovie[] = [];
      
      for (let page = 1; page <= Math.min(pages, 3); page++) { // Max 3 pages to avoid rate limiting
        const response = await fetch(
          `${TMDB_BASE_URL}/discover/movie?api_key=${this.apiKey}&with_original_language=${language}&sort_by=popularity.desc&page=${page}&vote_count.gte=100&include_adult=false`
        );
        
        if (!response.ok) continue;
        
        const data = await response.json();
        const movies = await Promise.all(
          data.results.map(async (movie: TMDbMovie) => await this.convertToStandardFormat(movie))
        );
        allMovies.push(...movies);
        
        if (allMovies.length >= count) break;
      }
      
      return allMovies.slice(0, count);
    } catch (error) {
      console.error(`Error fetching popular movies for language ${language}:`, error);
      return [];
    }
  }

  // Helper function to fetch general popular movies
  private async fetchPopularMovies(count: number): Promise<TMDbMovie[]> {
    try {
      const response = await fetch(
        `${TMDB_BASE_URL}/movie/popular?api_key=${this.apiKey}&page=1&include_adult=false`
      );
      
      if (!response.ok) return [];
      
      const data = await response.json();
      const movies = await Promise.all(
        data.results.slice(0, count).map(async (movie: TMDbMovie) => await this.convertToStandardFormat(movie))
      );
      
      return movies;
    } catch (error) {
      console.error('Error fetching general popular movies:', error);
      return [];
    }
  }

  // Helper function to fetch top-rated movies for a specific language
  private async fetchTopRatedMoviesByLanguage(language: string, count: number): Promise<TMDbMovie[]> {
    try {
      const pages = Math.ceil(count / 20); // TMDB returns ~20 movies per page
      const allMovies: TMDbMovie[] = [];
      
      for (let page = 1; page <= Math.min(pages, 3); page++) { // Max 3 pages to avoid rate limiting
        const response = await fetch(
          `${TMDB_BASE_URL}/discover/movie?api_key=${this.apiKey}&with_original_language=${language}&sort_by=vote_average.desc&page=${page}&vote_count.gte=1000&include_adult=false`
        );
        
        if (!response.ok) continue;
        
        const data = await response.json();
        const movies = await Promise.all(
          data.results.map(async (movie: TMDbMovie) => await this.convertToStandardFormat(movie))
        );
        allMovies.push(...movies);
        
        if (allMovies.length >= count) break;
      }
      
      return allMovies.slice(0, count);
    } catch (error) {
      console.error(`Error fetching top-rated movies for language ${language}:`, error);
      return [];
    }
  }

  // Helper function to fetch general top-rated movies
  private async fetchTopRatedMovies(count: number): Promise<TMDbMovie[]> {
    try {
      const response = await fetch(
        `${TMDB_BASE_URL}/movie/top_rated?api_key=${this.apiKey}&page=1&include_adult=false`
      );
      
      if (!response.ok) return [];
      
      const data = await response.json();
      const movies = await Promise.all(
        data.results.slice(0, count).map(async (movie: TMDbMovie) => await this.convertToStandardFormat(movie))
      );
      
      return movies;
    } catch (error) {
      console.error('Error fetching general top-rated movies:', error);
      return [];
    }
  }

  // Helper function to shuffle array
  private shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  // Get multiple posters for slider functionality
  getMultiplePosters(movie: TMDbMovie): string[] {
    const posters: string[] = [];
    
    // Add main poster
    if (movie.poster_path) {
      posters.push(`https://image.tmdb.org/t/p/w780${movie.poster_path}`);
    }
    
    // Add additional posters from images array
    if (movie.images?.posters) {
      const additionalPosters = movie.images.posters
        .filter(poster => poster.file_path !== movie.poster_path) // Don't duplicate main poster
        .sort((a, b) => b.vote_average - a.vote_average) // Sort by rating
        .slice(0, 5) // Take top 5 additional posters
        .map(poster => `https://image.tmdb.org/t/p/w780${poster.file_path}`);
      
      posters.push(...additionalPosters);
    }
    
    // Add backdrops as fallback posters if we don't have enough
    if (posters.length < 3 && movie.images?.backdrops) {
      const backdropPosters = movie.images.backdrops
        .sort((a, b) => b.vote_average - a.vote_average)
        .slice(0, 3 - posters.length)
        .map(backdrop => `https://image.tmdb.org/t/p/w780${backdrop.file_path}`);
      
      posters.push(...backdropPosters);
    }
    
    // Ensure we have at least 3 posters (for the slider)
    while (posters.length < 3 && movie.poster_path) {
      posters.push(`https://image.tmdb.org/t/p/w780${movie.poster_path}`);
    }
    
    return posters.slice(0, 5); // Maximum 5 posters for the slider
  }

  // Person-related methods

  async searchPeople(query: string, page: number = 1): Promise<TMDbPersonSearchResult> {
    try {
      // Use Firebase Functions if available
      if (shouldUseFunctionsAPI()) {
        return await tmdbFunctionsService.searchPeople(query, page);
      }

      // Fallback to direct API calls
      const isValid = await this.validateApiKey();
      if (!isValid) {
        return {
          page: 1,
          results: [],
          total_pages: 0,
          total_results: 0
        };
      }

      const response = await fetch(
        `${TMDB_BASE_URL}/search/person?api_key=${this.apiKey}&query=${encodeURIComponent(query)}&page=${page}&include_adult=false`
      );
      
      if (response.status === 401) {
        this.apiKeyValid = false;
        console.warn('TMDb API key is invalid. Person search functionality disabled.');
        return {
          page: 1,
          results: [],
          total_pages: 0,
          total_results: 0
        };
      }
      
      if (!response.ok) {
        throw new Error('Failed to search people');
      }
      
      const data: TMDbPersonSearchResult = await response.json();
      return data;
    } catch (error) {
      console.error('Error searching people:', error);
      throw error;
    }
  }

  async getPersonById(personId: string | number): Promise<TMDbPerson | null> {
    try {
      // Use Firebase Functions if available
      if (shouldUseFunctionsAPI()) {
        return await tmdbFunctionsService.getPersonById(personId);
      }

      // Fallback to direct API calls
      const isValid = await this.validateApiKey();
      if (!isValid) {
        console.warn(`TMDb API key invalid, returning null for person ${personId}`);
        return null;
      }

      const response = await fetch(
        `${TMDB_BASE_URL}/person/${personId}?api_key=${this.apiKey}&append_to_response=movie_credits,combined_credits,external_ids,images`
      );
      
      if (response.status === 401) {
        this.apiKeyValid = false;
        console.warn(`TMDb API key invalid for person ${personId}, returning null`);
        return null;
      }
      
      if (response.status === 404) {
        console.warn(`Person not found: ${personId}`);
        return null;
      }
      
      if (!response.ok) {
        throw new Error('Failed to fetch person details');
      }
      
      const personData: TMDbPerson = await response.json();
      return personData;
    } catch (error) {
      console.error('Error fetching person details:', error);
      return null;
    }
  }

  async getPersonByName(name: string): Promise<TMDbPerson | null> {
    try {
      // Use Firebase Functions if available
      if (shouldUseFunctionsAPI()) {
        return await tmdbFunctionsService.getPersonByName(name);
      }

      // Fallback to direct API calls
      const searchResults = await this.searchPeople(name);
      
      if (searchResults.results.length === 0) {
        console.warn(`Person not found: ${name}`);
        return null;
      }
      
      // Get the most popular match (first result is usually most relevant)
      const bestMatch = searchResults.results[0];
      return await this.getPersonById(bestMatch.id);
    } catch (error) {
      console.error('Error fetching person by name:', error);
      return null;
    }
  }

  getPersonProfileImage(profilePath: string | null, size: 'w185' | 'h632' | 'original' = 'h632'): string {
    if (!profilePath) {
      // Return a default avatar/profile image
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDMwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjNjY2Ii8+CjxjaXJjbGUgY3g9IjE1MCIgY3k9IjEyMCIgcj0iNDAiIGZpbGw9IiNjY2MiLz4KPHBhdGggZD0iTTEwMCAxODBDMTAwIDIwMCAxMjAgMjIwIDE1MCAyMjBDMTgwIDIyMCAyMDAgMjAwIDIwMCAxODBIMTAwWiIgZmlsbD0iI2NjYyIvPgo8L3N2Zz4K';
    }
    
    return `https://image.tmdb.org/t/p/${size}${profilePath}`;
  }

  formatPersonGender(gender: 0 | 1 | 2 | 3): string {
    switch (gender) {
      case 1: return 'Female';
      case 2: return 'Male';
      case 3: return 'Non-binary';
      default: return 'Not specified';
    }
  }

  formatPersonAge(birthday: string | null, deathday: string | null = null): string {
    if (!birthday) return 'Unknown';
    
    const birthDate = new Date(birthday);
    const endDate = deathday ? new Date(deathday) : new Date();
    const age = endDate.getFullYear() - birthDate.getFullYear();
    const monthDiff = endDate.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && endDate.getDate() < birthDate.getDate())) {
      return (age - 1).toString();
    }
    
    return age.toString();
  }

  getPersonTopMovies(person: TMDbPerson, limit: number = 10): Array<{
    id: number;
    title: string;
    character?: string;
    job?: string;
    poster_path: string | null;
    release_date: string;
    vote_average: number;
    popularity?: number;
    vote_count?: number;
  }> {
    if (!person.movie_credits) return [];

    // Combine cast and crew credits
    const allCredits = [
      ...person.movie_credits.cast.map(credit => ({
        ...credit,
        character: credit.character,
        job: undefined,
        isMainRole: credit.order !== undefined && credit.order < 10, // Top 10 billed actors
      })),
      ...person.movie_credits.crew.map(credit => ({
        ...credit,
        character: undefined,
        job: credit.job,
        isMainRole: ['Director', 'Producer', 'Screenplay', 'Writer'].includes(credit.job || ''),
      }))
    ];

    // Filter for quality movies with posters (strict filter)
    const filteredCredits = allCredits.filter(credit => {
      return (
        credit.poster_path &&
        credit.vote_average > 0 &&
        (credit.vote_count ?? 0) > 50 // Minimum vote count to ensure quality
      );
    });

    // If we don't have enough credits with strict filter, use a relaxed filter
    const creditsToScore = filteredCredits.length >= 4
      ? filteredCredits
      : allCredits.filter(credit => credit.poster_path); // Just require a poster

    // Calculate a "known for" score similar to TMDB's algorithm
    const scoredCredits = creditsToScore.map(credit => {
      let score = 0;

      // Popularity score (0-50 points) - TMDB uses this heavily
      const popularity = (credit as any).popularity || 0;
      score += Math.min(popularity * 2, 50);

      // Rating score (0-30 points)
      score += (credit.vote_average || 0) * 3;

      // Vote count score (0-20 points) - more votes = more well-known
      const voteCount = (credit as any).vote_count || 0;
      score += Math.min(Math.log10(voteCount + 1) * 5, 20);

      // Main role bonus (0-15 points)
      if ((credit as any).isMainRole) {
        score += 15;
      }

      // Recency bonus (0-10 points) - slight preference for recent work
      const releaseYear = credit.release_date ? parseInt(credit.release_date.split('-')[0]) : 0;
      const currentYear = new Date().getFullYear();
      const yearsSinceRelease = currentYear - releaseYear;
      if (yearsSinceRelease >= 0 && yearsSinceRelease <= 10) {
        score += Math.max(0, 10 - yearsSinceRelease);
      }

      return {
        ...credit,
        knownForScore: score
      };
    });

    // Sort by score and return top movies
    return scoredCredits
      .sort((a, b) => (b as any).knownForScore - (a as any).knownForScore)
      .slice(0, limit);
  }

  /**
   * Calculate relevance score for production company matches
   */
  private calculateProductionRelevance(movie: any, company: string, priority: number): number {
    let score = 0.6; // Base score for production match
    
    // Priority boost
    score += (priority / 10) * 0.2;
    
    // Popularity boost
    if (movie.popularity > 50) score += 0.1;
    if (movie.vote_average > 7) score += 0.1;
    
    return Math.min(score, 1.0);
  }

  /**
   * Calculate relevance score for keyword matches
   */
  private calculateKeywordRelevance(movie: any, keyword: string, priority: number): number {
    let score = 0.4; // Base score for keyword match
    
    // Title contains keyword
    if (movie.title.toLowerCase().includes(keyword.toLowerCase())) {
      score += 0.3;
    }
    
    // Overview contains keyword
    if (movie.overview && movie.overview.toLowerCase().includes(keyword.toLowerCase())) {
      score += 0.2;
    }
    
    // Priority boost
    score += (priority / 10) * 0.1;
    
    return Math.min(score, 1.0);
  }

  /**
   * Calculate relevance score for comprehensive search matches
   */
  private calculateComprehensiveRelevance(movie: any, query: string, analysis: QueryAnalysis): number {
    let score = 0.3; // Base score
    
    const lowerQuery = query.toLowerCase();
    const lowerTitle = movie.title.toLowerCase();
    const lowerOverview = movie.overview ? movie.overview.toLowerCase() : '';
    
    // Title relevance
    if (lowerTitle === lowerQuery) {
      score += 0.5;
    } else if (lowerTitle.includes(lowerQuery)) {
      score += 0.3;
    }
    
    // Overview relevance
    if (lowerOverview.includes(lowerQuery)) {
      score += 0.2;
    }
    
    // Keyword matches
    analysis.keywords.forEach(keyword => {
      if (lowerTitle.includes(keyword.toLowerCase())) {
        score += 0.1;
      }
      if (lowerOverview.includes(keyword.toLowerCase())) {
        score += 0.05;
      }
    });
    
    // Popularity and rating boost
    if (movie.popularity > 30) score += 0.1;
    if (movie.vote_average > 6) score += 0.1;
    
    return Math.min(score, 1.0);
  }

  // Get top movies for a specific genre with language preferences and proper mixing
  async getTopMoviesForGenreWithLanguages(
    genreId: number, 
    userLanguages: string[] = ['en'], 
    totalCount: number = 20
  ): Promise<TMDbMovie[]> {
    try {
      console.log(`🎭 TMDbService: Fetching ${totalCount} movies for genre ${genreId} with languages: ${userLanguages.join(', ')}`);
      
      const allMovies: TMDbMovie[] = [];
      const seenMovieIds = new Set<number>();
      
      // Calculate distribution - prioritize English but ensure variety
      const hasEnglish = userLanguages.includes('en');
      const otherLanguages = userLanguages.filter(lang => lang !== 'en');
      
      let englishCount = hasEnglish ? Math.ceil(totalCount * 0.5) : 0; // 50% English if selected
      let otherLanguagesCount = totalCount - englishCount;
      
      // If only English is selected, get all movies in English
      if (userLanguages.length === 1 && userLanguages[0] === 'en') {
        englishCount = totalCount;
        otherLanguagesCount = 0;
      }
      
      console.log(`🎯 Target distribution - English: ${englishCount}, Other languages: ${otherLanguagesCount}`);
      
      // Fetch English movies if needed
      if (englishCount > 0) {
        const englishMovies = await this.fetchGenreMoviesByLanguage(genreId, 'en', Math.ceil(englishCount * 1.5));
        
        for (const movie of englishMovies) {
          if (allMovies.length >= totalCount) break;
          if (!seenMovieIds.has(movie.id) && allMovies.filter(m => m.original_language === 'en').length < englishCount) {
            // Verify movie actually belongs to the genre
            if (this.verifyMovieGenre(movie, genreId)) {
              allMovies.push(movie);
              seenMovieIds.add(movie.id);
            }
          }
        }
      }
      
      // Fetch movies from other selected languages
      if (otherLanguages.length > 0 && otherLanguagesCount > 0) {
        const moviesPerLanguage = Math.ceil(otherLanguagesCount / otherLanguages.length);
        
        for (const language of otherLanguages) {
          console.log(`🌍 Fetching ${moviesPerLanguage} movies in ${language}`);
          const languageMovies = await this.fetchGenreMoviesByLanguage(genreId, language, Math.ceil(moviesPerLanguage * 1.5));
          
          let addedFromThisLanguage = 0;
          for (const movie of languageMovies) {
            if (allMovies.length >= totalCount) break;
            if (addedFromThisLanguage >= moviesPerLanguage) break;
            
            if (!seenMovieIds.has(movie.id)) {
              // Verify movie actually belongs to the genre
              if (this.verifyMovieGenre(movie, genreId)) {
                allMovies.push(movie);
                seenMovieIds.add(movie.id);
                addedFromThisLanguage++;
              }
            }
          }
        }
      }
      
      // If we still don't have enough movies, fill with popular movies from the genre (any language)
      if (allMovies.length < totalCount) {
        console.log(`🔄 Need ${totalCount - allMovies.length} more movies, fetching popular genre movies`);
        const popularMovies = await this.fetchGenreMoviesByLanguage(genreId, '', Math.ceil((totalCount - allMovies.length) * 1.5));
        
        for (const movie of popularMovies) {
          if (allMovies.length >= totalCount) break;
          if (!seenMovieIds.has(movie.id)) {
            // Verify movie actually belongs to the genre
            if (this.verifyMovieGenre(movie, genreId)) {
              allMovies.push(movie);
              seenMovieIds.add(movie.id);
            }
          }
        }
      }
      
      // Shuffle movies to create an unordered mix while maintaining some language distribution
      const shuffledMovies = this.createLanguageMix(allMovies, userLanguages);
      
      console.log(`✅ Final genre ${genreId} results:`, {
        total: shuffledMovies.length,
        languages: shuffledMovies.reduce((acc, movie) => {
          acc[movie.original_language] = (acc[movie.original_language] || 0) + 1;
          return acc;
        }, {} as Record<string, number>)
      });
      
      return shuffledMovies.slice(0, totalCount);
    } catch (error) {
      console.error('Error fetching top movies for genre with languages:', error);
      // Fallback to original method
      return this.getTopMoviesForGenre(genreId, totalCount);
    }
  }

  // Helper method to fetch movies by genre and language
  private async fetchGenreMoviesByLanguage(genreId: number, language: string, limit: number = 20): Promise<TMDbMovie[]> {
    try {
      const languageParam = language ? `&with_original_language=${language}` : '';
      const response = await fetch(
        `${TMDB_BASE_URL}/discover/movie?api_key=${this.apiKey}&with_genres=${genreId}&sort_by=popularity.desc&vote_count.gte=50${languageParam}&page=1&include_adult=false`
      );
      
      if (!response.ok) {
        return [];
      }
      
      const data = await response.json();
      const movies = data.results || [];
      
      // Get multiple pages if needed
      let allMovies = [...movies];
      if (allMovies.length < limit && data.total_pages > 1) {
        const page2Response = await fetch(
          `${TMDB_BASE_URL}/discover/movie?api_key=${this.apiKey}&with_genres=${genreId}&sort_by=popularity.desc&vote_count.gte=50${languageParam}&page=2&include_adult=false`
        );
        
        if (page2Response.ok) {
          const page2Data = await page2Response.json();
          allMovies.push(...(page2Data.results || []));
        }
      }
      
      // Fetch detailed data for movies
      const detailedMovies = await Promise.all(
        allMovies.slice(0, limit).map(async (movie: any) => {
          try {
            const detailResponse = await fetch(
              `${TMDB_BASE_URL}/movie/${movie.id}?api_key=${this.apiKey}&append_to_response=credits,external_ids`
            );
            
            if (!detailResponse.ok) {
              return {
                ...movie,
                year: movie.release_date ? new Date(movie.release_date).getFullYear().toString() : '',
                runtime: null,
                imdb_id: null,
                genres: []
              };
            }
            
            const detailData = await detailResponse.json();
            return {
              ...detailData,
              year: detailData.release_date ? new Date(detailData.release_date).getFullYear().toString() : ''
            };
          } catch (error) {
            console.error(`Error fetching details for movie ${movie.id}:`, error);
            return {
              ...movie,
              year: movie.release_date ? new Date(movie.release_date).getFullYear().toString() : '',
              runtime: null,
              imdb_id: null,
              genres: []
            };
          }
        })
      );
      
      return detailedMovies;
    } catch (error) {
      console.error(`Error fetching genre movies for language ${language}:`, error);
      return [];
    }
  }

  // Helper method to verify a movie actually belongs to the specified genre
  private verifyMovieGenre(movie: any, genreId: number): boolean {
    // Check if movie has genres and the specified genre is included
    if (movie.genres && Array.isArray(movie.genres)) {
      return movie.genres.some((genre: any) => genre.id === genreId);
    }
    
    // Fallback: check genre_ids if genres array is not available
    if (movie.genre_ids && Array.isArray(movie.genre_ids)) {
      return movie.genre_ids.includes(genreId);
    }
    
    // If no genre information, assume it's valid (better than filtering out)
    return true;
  }

  // Helper method to create a balanced language mix
  private createLanguageMix(movies: TMDbMovie[], userLanguages: string[]): TMDbMovie[] {
    // Group movies by language
    const moviesByLanguage: Record<string, TMDbMovie[]> = {};
    
    for (const movie of movies) {
      const lang = movie.original_language || 'unknown';
      if (!moviesByLanguage[lang]) {
        moviesByLanguage[lang] = [];
      }
      moviesByLanguage[lang].push(movie);
    }
    
    // Create mixed array - alternate between languages when possible
    const mixedMovies: TMDbMovie[] = [];
    const languageQueues = Object.keys(moviesByLanguage).map(lang => ({
      language: lang,
      movies: [...moviesByLanguage[lang]], // Copy array
      priority: userLanguages.includes(lang) ? 1 : 2 // Prioritize user's selected languages
    })).sort((a, b) => a.priority - b.priority);
    
    // Round-robin through languages
    let currentLanguageIndex = 0;
    while (mixedMovies.length < movies.length) {
      let addedThisRound = false;
      
      for (let i = 0; i < languageQueues.length; i++) {
        const queueIndex = (currentLanguageIndex + i) % languageQueues.length;
        const queue = languageQueues[queueIndex];
        
        if (queue.movies.length > 0) {
          mixedMovies.push(queue.movies.shift()!);
          addedThisRound = true;
          
          if (mixedMovies.length >= movies.length) break;
        }
      }
      
      if (!addedThisRound) break; // No more movies available
      currentLanguageIndex = (currentLanguageIndex + 1) % languageQueues.length;
    }
    
    return mixedMovies;
  }

  /**
   * Batch fetch movie data for multiple movies with caching and error handling
   * Uses tmdbRequestQueue which implements: local cache -> Firestore cache -> TMDB API
   */
  async getBatchMovieData(
    movieIds: (string | number)[],
    fallbackDataMap?: Map<string, any>
  ): Promise<(ExtendedMovie | null)[]> {
    console.log(`🎬 TMDbService: Batch fetching ${movieIds.length} movies (using cache-first strategy)`);

    // Use the request queue which handles:
    // 1. Local in-memory cache (fastest)
    // 2. Firestore cache (shared across users)
    // 3. TMDB API with rate limiting (last resort)
    const movieIdStrings = movieIds.map(id => id.toString());
    const cachedResults = await tmdbRequestQueue.getMoviesBatch(movieIdStrings);

    // Convert results to ExtendedMovie format
    const results: (ExtendedMovie | null)[] = cachedResults.map((movie, index) => {
      if (movie && (movie.Response === 'True' || movie.title)) {
        return this.convertToExtendedMovie(movie as TMDbMovie);
      }

      // Try fallback data if available
      const movieId = movieIdStrings[index];
      if (fallbackDataMap && fallbackDataMap.has(movieId)) {
        const fallbackData = fallbackDataMap.get(movieId);
        return this.createPlaceholderExtendedMovie(movieId, fallbackData);
      }

      return null;
    });

    const successCount = results.filter(r => r !== null).length;
    console.log(`✅ TMDbService: Completed fetching ${successCount}/${movieIds.length} movies`);
    return results;
  }

  /**
   * Convert TMDbMovie to ExtendedMovie format
   */
  private convertToExtendedMovie(tmdbMovie: TMDbMovie): ExtendedMovie {
    return {
      ...tmdbMovie,
      // Legacy compatibility fields
      imdbID: tmdbMovie.imdb_id || `tmdb_${tmdbMovie.id}`,
      Title: tmdbMovie.title,
      Year: tmdbMovie.year || (tmdbMovie.release_date ? tmdbMovie.release_date.split('-')[0] : ''),
      Poster: tmdbMovie.poster_path ? `https://image.tmdb.org/t/p/w500${tmdbMovie.poster_path}` : 'N/A',
      Plot: tmdbMovie.overview,
      Genre: tmdbMovie.genres?.map(g => g.name).join(', ') || '',
      Director: tmdbMovie.credits?.crew?.find(c => c.job === 'Director')?.name || '',
      Actors: tmdbMovie.credits?.cast?.slice(0, 3).map(c => c.name).join(', ') || '',
      Runtime: tmdbMovie.runtime ? `${tmdbMovie.runtime} min` : '',
      imdbRating: tmdbMovie.vote_average ? tmdbMovie.vote_average.toFixed(1) : '',
      Response: 'True'
    };
  }

  /**
   * Create placeholder ExtendedMovie for failed requests
   */
  private createPlaceholderExtendedMovie(movieId: string, fallbackData?: any): ExtendedMovie {
    const baseMovie = this.createFallbackMovie(movieId, fallbackData?.title);
    return this.convertToExtendedMovie(baseMovie);
  }
}

export interface ExtendedMovie extends TMDbMovie {
  watched?: boolean;
  addedOn?: string;
  watchedOn?: string;
  addedBy?: string;
  note?: string;
  cached?: boolean;
  // Legacy compatibility fields
  imdbID?: string;
  Title?: string;
  Year?: string;
  Poster?: string;
  Plot?: string;
  Genre?: string;
  Director?: string;
  Actors?: string;
  Runtime?: string;
  imdbRating?: string;
  Response?: string;
}

export const tmdbService = new TMDbService(); 
